import React, { useEffect, useState, useMemo, useCallback, useRef, createContext, useContext } from "react";
import { DateTime } from "luxon";
import { weekColumnsForReset } from "./components/AvailabilityGrid";
import { formatWeekRange, formatWeekStartShort } from "./lib/regionReset";
import GuildLootImportsTab from "./GuildLootImportsTab";
import GuildLootLedgerTab from "./GuildLootLedgerTab";
import GuildConsumablesTab from "./GuildConsumablesTab";
import { useConfirm } from "./ConfirmDialog";
import { useTimezone } from "./lib/TimezoneContext";
import {
  formatCountdownToMs,
  formatHomeRaidSessionsLabel,
  formatRaidTime,
  getCurrentResetWeek,
  getNextResetWeek,
  localCellToBitIdx,
  ResetRule,
} from "./lib/time";

import { tc, useLegible } from "./theme/legible";
import { shortGameName } from "./lib/gameName";
// ══════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════

type RaidAssignedChar = {
  character_id: string;
  character_name: string;
  class_name: string;
  primary_spec_label: string | null;
  primary_role: string;
  spec_labels: string[];
  display_name: string | null;
  role_label: string | null;
  wcl_score: number | null;
  wcl_parse?: WclParse | null;
};

/** Provenance of a stored parse: which spec and metric the percentile measures. */
type WclParse = {
  spec: string | null;
  metric: string | null;
  spec_matched: boolean;
  encounters: number | null;
  zone_name: string | null;
};

/** "Fury · DPS · 10 bosses · SSC / TK" — hover text for a bare parse number. */
function parseTooltip(score: number, p?: WclParse | null): string {
  if (!p) return `${score} parse`;
  const bits = [`${score} parse`];
  if (p.spec) bits.push(p.spec_matched ? p.spec : `${p.spec} (not your listed spec)`);
  if (p.metric) bits.push(p.metric.toUpperCase());
  if (p.encounters) bits.push(`${p.encounters} boss${p.encounters === 1 ? "" : "es"}`);
  if (p.zone_name) bits.push(p.zone_name);
  return bits.join(" · ");
}

type DashboardRaid = {
  id: string;
  name: string;
  state: string;
  raidType: string | null;
  raidImage: string | null;
  raidSize: number | null;
  nextSession: string | null;
  sessions: { starts_at: string; duration: number }[];
  assignedCount: number;
  hasRaidLeader: boolean;
  raidLeaderName: string | null;
  raidLeaderClass: string | null;
  hasLootMaster: boolean;
  lootMasterName: string | null;
  lootMasterClass: string | null;
  srStatus: { enabled: boolean; is_locked: boolean; total_reserves: number; is_blind: boolean } | null;
  roster: RaidAssignedChar[];
};

type DashboardMember = {
  characterId: string;
  characterName: string;
  userId: string;
  displayName: string;
  metadata: any;
  ilvl: number | null;
  isOfficer: boolean;
  joinedAt: string;
  availabilityStatus: "confirmed" | "set" | "stale" | "missing";
  currentWeekBits: string | null;
  nextWeekBits: string | null;
  currentWeekConfirmed: boolean;
  nextWeekConfirmed: boolean;
  raidAssignments: { raid_id: string; raid_name: string; role_label: string }[];
  options: { group_id: number; option_id: number }[];
  totalRatings: number;
  overallRating: number | null;
  hasLowRatings: boolean;
};

type FieldOption = { id: number; value_key: string; label: string; metadata?: any; image_url?: string | null };
type FieldGroup = { group_id: number; key: string; label: string; multi_select: boolean; options: FieldOption[] };

type DashboardData = {
  guild: {
    name: string;
    tag: string;
    faction: string;
    serverName: string;
    logoUrl: string | null;
    memberCount: number;
    gameCode?: string | null;
  };
  raids: DashboardRaid[];
  members: DashboardMember[];
  fields: FieldGroup[];
  // Games the guild plays (all equal) and the one this data is for.
  gameId?: string | null;
  games?: { gameId: string; gameName: string; isActive: boolean }[];
  weekInfo: {
    currentWeek: string;
    nextWeek: string;
    /** ISO weekday the guild's week starts on: 3 = Wednesday (EU), 2 = Tuesday (US). */
    resetWeekdayIso?: number;
    resetDayName?: string;
    /** The guild's full reset rule. Absent from an older server, then EU. */
    resetRule?: ResetRule;
  };
};

// ══════════════════════════════════════════════════════════
// COLOR SYSTEM
// ══════════════════════════════════════════════════════════

const CC: Record<string, string> = {
  warrior: "#C79C6E", paladin: "#F58CBA", hunter: "#ABD473", rogue: "#FFF569",
  priest: "#FFFFFF", shaman: "#0070DE", mage: "#69CCF0", warlock: "#9482C9",
  druid: "#FF7D0A", "death knight": "#C41F3B",
};
const RC: Record<string, { bg: string; tx: string; bd: string }> = {
  tank:       { bg: "rgba(56,189,248,0.10)", tx: "#38bdf8", bd: "rgba(56,189,248,0.25)" },
  healer:     { bg: "rgba(52,211,153,0.10)", tx: "#34d399", bd: "rgba(52,211,153,0.25)" },
  melee_dps:  { bg: "rgba(251,191,36,0.10)", tx: "#fbbf24", bd: "rgba(251,191,36,0.25)" },
  ranged_dps: { bg: "rgba(167,139,250,0.10)", tx: "#a78bfa", bd: "rgba(167,139,250,0.25)" },
};

function cc(cn: string | null | undefined): string { return cn ? CC[cn.toLowerCase()] || "#666" : "#666"; }
function rc(role: string | null | undefined) {
  if (!role) return RC.melee_dps;
  const r = role.toLowerCase();
  return r.includes("tank") ? RC.tank : r.includes("heal") ? RC.healer : r.includes("melee") ? RC.melee_dps : r.includes("ranged") ? RC.ranged_dps : RC.melee_dps;
}
function ri(role: string | null | undefined): string {
  if (!role) return "⚔️";
  const r = role.toLowerCase();
  return r.includes("tank") ? "🛡️" : r.includes("heal") ? "💚" : r.includes("melee") ? "⚔️" : r.includes("ranged") ? "🏹" : "⚔️";
}
function parseColor(s: number | null): string {
  if (s === null || s === undefined) return "#444";
  if (s === 100) return "#e5cc80"; if (s >= 99) return "#e268a8"; if (s >= 95) return "#ff8000"; if (s >= 75) return "#a335ee"; if (s >= 50) return "#0070dd"; if (s >= 25) return "#1eff00"; return "#9d9d9d";
}
function fmtRole(r: string): string { return r ? r.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ") : ""; }
function fmtCountdown(iso: string): { text: string; urgent: boolean } {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diff = t - now;
  if (diff <= 0) return { text: "LIVE", urgent: true };
  const text = formatCountdownToMs(t, now);
  const dd = Math.floor(diff / 864e5);
  const hh = Math.floor((diff % 864e5) / 36e5);
  const urgent = dd === 0 && (hh > 0 ? hh < 2 : true);
  return { text, urgent };
}
/** One-line raid schedule in the viewer's IANA zone (API `starts_at` / `nextSession` as UTC instants). */
function dashboardRaidScheduleOneLine(raid: DashboardRaid, userTz: string): string | null {
  if (raid.sessions && raid.sessions.length > 0) {
    return formatHomeRaidSessionsLabel(
      raid.sessions.map((s) => ({ starts_at: s.starts_at })),
      null,
      userTz
    );
  }
  if (raid.nextSession) {
    const fr = formatRaidTime(raid.nextSession, userTz);
    return `${fr.date} · ${fr.time}`;
  }
  return null;
}
/** Reset week `YYYY-MM-DD` (Wed UTC) → local calendar span label. */
// Officer-facing week range. `weekStartYmd` is the storage label (a Wednesday in
// every region), so it is resolved to the guild's real reset day first —
// otherwise a US guild's officers read a span starting a day after it really did.
// Rendered in the rule's zone, not the officer's, so the realm's reset day holds.
function fmtWeek(weekStartYmd: string, _userTz: string, weekdayIso: number = 3): string {
  const label = String(weekStartYmd).slice(0, 10);
  return formatWeekRange(label, { weekday: weekdayIso, hour: 0, tz: "UTC" }).replace(" - ", " – ");
}
// Single-date week chip. Same rule as fmtWeek: resolve the storage label to the
// guild's real reset day before showing it.
function fmtWeekLabel(d: string | null | undefined, weekdayIso: number = 3): string {
  if (d == null || String(d).trim() === "") return "—";
  const s = String(d).trim();
  const ymd = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return formatWeekStartShort(ymd, { weekday: weekdayIso, hour: 0, tz: "UTC" });
  }
  // API may send `YYYY-MM-DD` (date) or full ISO from Postgres; do not append `T00:00:00Z` twice.
  const forParse = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s;
  const dt = new Date(forParse);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function getMemberFieldValues(m: DashboardMember, fields: FieldGroup[], key: string): FieldOption[] {
  const g = fields.find(f => f.key === key);
  if (!g) return [];
  return m.options.filter(o => Number(o.group_id) === Number(g.group_id)).map(o => g.options.find(fo => fo.id === Number(o.option_id))).filter(Boolean) as FieldOption[];
}

// ══════════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ══════════════════════════════════════════════════════════

function Tip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className="relative inline-flex"
      onMouseEnter={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPos({ x: rect.left + rect.width / 2, y: rect.top });
        setShow(true);
      }}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          className="fixed z-[100] pointer-events-none"
          style={{ left: pos.x, top: pos.y - 8, transform: "translate(-50%, -100%)" }}
        >
          <div className="bg-[var(--c-sunken)] border border-[var(--c-line-strong)] text-[var(--c-ink)] text-[11px] leading-relaxed px-3 py-2 rounded-lg shadow-xl shadow-black/40 max-w-[240px] backdrop-blur-sm">
            {text}
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-[var(--c-sunken)] border-r border-b border-[var(--c-line-strong)] rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// AVAILABILITY MINI HEATMAP
// ══════════════════════════════════════════════════════════

// Column order for every availability grid on this page. Rotated to open on the
// GUILD's reset day: Wednesday for EU, Tuesday for US.
//
// It is a context rather than a constant because these grids are officer-facing
// — an officer in Berlin editing a US player must see that PLAYER's week, not
// their own. The guild is single-region (candidates are hard-filtered on region,
// realm and faction), so one order is correct for the whole roster.
//
// Purely presentational: the stored 336-bit string is Monday-anchored and never
// moves, and localCellToBitIdx maps each column back to its canonical bit.
const AvailColumnsContext = createContext(weekColumnsForReset(3));

function useAvailColumns(): { days: string[]; dayLocalMon0: number[] } {
  const cols = useContext(AvailColumnsContext);
  return {
    days: cols.daysDisplay,
    dayLocalMon0: cols.displayDayOrderLocalMon0,
  };
}

function availBitOn(bits: string | null | undefined, idx: number): boolean {
  return !!bits && idx >= 0 && idx < bits.length && bits[idx] === "1";
}

function AvailMini({
  currentBits,
  nextBits,
  weekCurrentIso,
  weekNextIso,
  scheduleWeek,
}: {
  currentBits: string | null;
  nextBits: string | null;
  weekCurrentIso: string;
  weekNextIso: string;
  /** When set (squads tab), mini matches the week toggle. When omitted, prefers next week if present (roster column). */
  scheduleWeek?: "current" | "next";
}) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  let bits: string | null = null;
  let weekIso = String(weekCurrentIso).slice(0, 10);
  if (scheduleWeek === "current") {
    bits = currentBits && currentBits.length >= 336 ? currentBits : null;
    weekIso = String(weekCurrentIso).slice(0, 10);
  } else if (scheduleWeek === "next") {
    bits = nextBits && nextBits.length >= 336 ? nextBits : null;
    weekIso = String(weekNextIso).slice(0, 10);
  } else {
    const useNext = !!(nextBits && nextBits.length >= 336);
    bits = useNext ? nextBits : currentBits;
    weekIso = String(useNext ? weekNextIso : weekCurrentIso).slice(0, 10);
  }
  if (!bits || bits.length < 336) return <span className="text-[9px] text-[var(--c-ink-4)]">—</span>;
  return (
    <div className="flex gap-[1px]">
      {availDays.map((dayName, displayIdx) => {
        const dayLocalMon0 = availDayLocalMon0[displayIdx];
        const active = Array.from({ length: 48 }).reduce((acc: number, _, s) => {
          const bi = localCellToBitIdx(weekIso, dayLocalMon0, s, userTz);
          return acc + (availBitOn(bits, bi) ? 1 : 0);
        }, 0);
        const pct = active / 48;
        return (
          <div
            key={dayName}
            className="w-[5px] h-3 rounded-[1px]"
            style={{
              backgroundColor: pct === 0 ? "rgba(255,255,255,0.04)"
                : pct < 0.25 ? "rgba(52,211,153,0.25)"
                : pct < 0.5 ? "rgba(52,211,153,0.45)"
                : pct < 0.75 ? "rgba(52,211,153,0.65)"
                : "rgba(52,211,153,0.9)"
            }}
            title={`${dayName}: ${active} slots`}
          />
        );
      })}
    </div>
  );
}

function DualWeekAvailGrid({ currentWeekBits, nextWeekBits, weekInfo }: { currentWeekBits: string | null; nextWeekBits: string | null; weekInfo: { currentWeek: string; nextWeek: string; resetWeekdayIso?: number } }) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  const hasCurrent = currentWeekBits && currentWeekBits.length === 336;
  const hasNext = nextWeekBits && nextWeekBits.length === 336;
  if (!hasCurrent && !hasNext) return <div className="flex items-center justify-center h-12 bg-[var(--c-bg-veil)] rounded-lg border border-[var(--c-line-soft)] text-[var(--c-ink-4)] text-xs italic">No availability data</div>;
  const weekCurrentIso = String(weekInfo.currentWeek).slice(0, 10);
  const weekNextIso = String(weekInfo.nextWeek).slice(0, 10);
  const getTimeLabel = (slotIdx: number) =>
    `${Math.floor(slotIdx / 2).toString().padStart(2, "0")}:${slotIdx % 2 === 0 ? "00" : "30"}`;
  return (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-4 px-1">
        <span className="text-xs font-bold uppercase text-[var(--c-ink-4)] tracking-wider">Availability</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-sm bg-cyan-500" /><span className="text-[10px] text-[var(--c-ink-3)]">Current ({fmtWeekLabel(weekInfo.currentWeek, weekInfo.resetWeekdayIso)})</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-sm bg-amber-500" /><span className="text-[10px] text-[var(--c-ink-3)]">Next ({fmtWeekLabel(weekInfo.nextWeek, weekInfo.resetWeekdayIso)})</span></div>
        </div>
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-line-soft)] rounded-lg p-3 space-y-0.5">
        <div className="flex pl-8 text-[9px] text-[var(--c-ink-4)] font-mono justify-between select-none px-0.5 mb-1"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
        {availDays.map((day, displayIdx) => {
          const dayLocalMon0 = availDayLocalMon0[displayIdx];
          const active = Array.from({ length: 48 }).some((_, s) => {
            const iCur = localCellToBitIdx(weekCurrentIso, dayLocalMon0, s, userTz);
            const iNext = localCellToBitIdx(weekNextIso, dayLocalMon0, s, userTz);
            return (hasCurrent && availBitOn(currentWeekBits, iCur)) || (hasNext && availBitOn(nextWeekBits, iNext));
          });
          return (
            <div key={day} className="flex items-center gap-1">
              <div className={`w-7 text-[10px] font-bold ${active ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-4)]"}`}>{day}</div>
              <div className="flex-1 flex flex-col gap-[1px]">
                <div className="h-[5px] bg-[var(--c-bg-veil)] rounded-sm overflow-hidden flex">
                  {hasCurrent && Array.from({ length: 48 }).map((_, s) => {
                    const dataIdx = localCellToBitIdx(weekCurrentIso, dayLocalMon0, s, userTz);
                    const on = availBitOn(currentWeekBits, dataIdx);
                    return (
                      <div
                        key={s}
                        className="flex-1"
                        style={{ backgroundColor: on ? "#06b6d4" : "transparent" }}
                        title={on ? `${day} ${getTimeLabel(s)} — Current` : undefined}
                      />
                    );
                  })}
                </div>
                <div className="h-[5px] bg-[var(--c-bg-veil)] rounded-sm overflow-hidden flex">
                  {hasNext && Array.from({ length: 48 }).map((_, s) => {
                    const dataIdx = localCellToBitIdx(weekNextIso, dayLocalMon0, s, userTz);
                    const on = availBitOn(nextWeekBits, dataIdx);
                    return (
                      <div
                        key={s}
                        className="flex-1"
                        style={{ backgroundColor: on ? "#f59e0b" : "transparent" }}
                        title={on ? `${day} ${getTimeLabel(s)} — Next` : undefined}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// RADIAL RING
// ══════════════════════════════════════════════════════════

function Ring({ pct, size = 56, stroke = 3, color, children }: {
  pct: number; size?: number; stroke?: number; color: string; children?: React.ReactNode;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * circ} ${circ}`}
          className="transition-all duration-700"
          style={pct >= 100 ? { filter: `drop-shadow(0 0 4px ${color})` } : {}}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════

type TabKey = "overview" | "raids" | "roster" | "squads" | "attendance" | "loot" | "ledger" | "consumables" | "requests";

type JoinRequestRow = {
  id: string;
  requestedAt: string;
  characterId: string;
  characterName: string | null;
  displayName: string | null;
  source?: string;
  currentGuildName?: string | null;
  className: string | null;
  classKey: string | null;
  primarySpecLabel: string | null;
  specLabels: string[];
  roleLabels: string[];
  wclScore?: number | null;
  wclUrl?: string | null;
};

export default function GuildDashboard({ guildId }: { guildId: string }) {
  useLegible();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    // Deep-link from the home-page Command Center: open straight on the
    // requested sub-tab, then clear the flag so a manual reload returns to
    // the default Overview view.
    const requested = sessionStorage.getItem('openDashboardSubtab');
    if (
      requested === 'overview' || requested === 'raids' ||
      requested === 'roster'   || requested === 'squads' ||
      requested === 'attendance' || requested === 'loot' ||
      requested === 'ledger' || requested === 'consumables' ||
      requested === 'requests'
    ) {
      sessionStorage.removeItem('openDashboardSubtab');
      return requested;
    }
    return "overview";
  });

  // Same deep-link, but for callers that arrive while the dashboard is already
  // mounted (the guild setup tour walking someone to "Recruit from Discord").
  useEffect(() => {
    const onSubtab = (e: Event) => {
      const tab = (e as CustomEvent).detail?.tab as TabKey | undefined;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener("lfm:dashboard-subtab", onSubtab);
    return () => window.removeEventListener("lfm:dashboard-subtab", onSubtab);
  }, []);

  // Which of the guild's games the dashboard shows ("" = let the server pick).
  const [dashGameId, setDashGameId] = useState<string>("");
  useEffect(() => { setDashGameId(""); }, [guildId]);

  const loadDashboard = useCallback(() => {
    setLoading(true); setError(null);
    const q = dashGameId ? `?gameId=${encodeURIComponent(dashGameId)}` : "";
    fetch(`/api/guilds/${guildId}/dashboard${q}`, { credentials: "include" })
      .then(r => {
        if (r.status === 403) throw new Error("forbidden");
        if (!r.ok) return r.text().then(t => { console.error('[Dashboard]', r.status, t); throw new Error("fetch_failed"); });
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [guildId, dashGameId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Pending join applications (Discord "join through guild" flow). Loaded at
  // this level so the Requests tab can show a count badge.
  const [joinRequests, setJoinRequests] = useState<JoinRequestRow[]>([]);
  const loadJoinRequests = useCallback(() => {
    fetch(`/guilds/${guildId}/join-requests`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(rows => setJoinRequests(Array.isArray(rows) ? rows : []))
      .catch(() => setJoinRequests([]));
  }, [guildId]);
  useEffect(() => { loadJoinRequests(); }, [loadJoinRequests]);

  const stats = useMemo(() => {
    if (!data) return null;
    const { members, raids, fields } = data;
    // Spec counts keyed by "ClassName SpecLabel" for uniqueness
    const specCounts: Record<string, { count: number; role: string; className: string; specLabel: string }> = {};
    const classCounts: Record<string, number> = {};
    let availConfirmed = 0, availSet = 0, availStale = 0, availMissing = 0;
    let unrostered = 0, willingRL = 0, willingLM = 0;

    members.forEach(m => {
      const cn = m.metadata?.class_name || "";
      const specLabel = m.metadata?.primary_spec_label || "";
      const role = m.metadata?.primary_role || "";
      // Use "Class Spec" as key to disambiguate Protection Paladin vs Protection Warrior
      const specKey = specLabel ? `${cn} ${specLabel}` : cn || "Unknown";
      if (!specCounts[specKey]) specCounts[specKey] = { count: 0, role, className: cn, specLabel };
      specCounts[specKey].count++;
      if (cn) classCounts[cn] = (classCounts[cn] || 0) + 1;
      switch (m.availabilityStatus) {
        case "confirmed": availConfirmed++; break;
        case "set": availSet++; break;
        case "stale": availStale++; break;
        default: availMissing++;
      }
      if (!m.raidAssignments || m.raidAssignments.length === 0) unrostered++;
      const prefs = getMemberFieldValues(m, fields, "raid_preferences");
      prefs.forEach(p => {
        if (p.value_key === "willing_raid_lead") willingRL++;
        if (p.value_key === "willing_loot_master") willingLM++;
      });
    });
    const totalAssigned = members.length - unrostered;
    return {
      totalMembers: members.length, totalRaids: raids.length, specCounts, classCounts,
      availConfirmed, availSet, availStale, availMissing,
      unrostered, totalAssigned,
      rosterPct: members.length > 0 ? Math.round((totalAssigned / members.length) * 100) : 0,
      confirmedPct: members.length > 0 ? Math.round((availConfirmed / members.length) * 100) : 0,
      willingRL, willingLM,
    };
  }, [data]);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-2 border-[var(--c-line-soft)]" />
          <div className="absolute inset-0 rounded-full border-2 border-t-cyan-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
        </div>
        <span className="text-xs text-[var(--c-ink-4)] uppercase tracking-[0.2em]">Loading Dashboard</span>
      </div>
    </div>
  );

  if (error === "forbidden") return (
    <div className="py-20 text-center">
      <div className="text-4xl mb-4 opacity-60">🔒</div>
      <div className="text-sm text-[var(--c-ink-3)] font-medium">Dashboard access is restricted to officers and owners.</div>
    </div>
  );

  if (error || !data || !stats) return (
    <div className="py-20 text-center">
      <div className="text-4xl mb-4 opacity-60">⚠️</div>
      <div className="text-sm text-[var(--c-ink-3)]">Failed to load dashboard data.</div>
      <div className="text-xs text-[var(--c-ink-4)] mt-1">Check the browser console for details.</div>
    </div>
  );

  const TABS: { key: TabKey; label: string; icon: string }[] = [
    { key: "overview", label: "Overview", icon: "📊" },
    { key: "raids",    label: "Raids",    icon: "⚔️" },
    { key: "roster",   label: "Roster",   icon: "👥" },
    { key: "squads",   label: "Squads",   icon: "🤝" },
    { key: "attendance", label: "Attendance", icon: "📅" },
    { key: "loot", label: "Loot imports", icon: "📜" },
    { key: "ledger", label: "Loot ledger", icon: "🗂️" },
    { key: "consumables", label: "Consumables", icon: "🧪" },
    { key: "requests", label: "Requests", icon: "📥" },
  ];

  // Every availability grid below opens on the GUILD's reset day. Falls back to
  // Wednesday (EU) while data is loading or if an older server omits the field.
  const availColumns = weekColumnsForReset(data?.weekInfo?.resetWeekdayIso ?? 3);

  return (
    <AvailColumnsContext.Provider value={availColumns}>
    <div className="space-y-4 -mx-2 sm:-mx-4">
      {/* Game switcher: only when the guild plays more than one game. Raids,
          roster and game data below all follow it. */}
      {(data.games?.length || 0) > 1 && (
        <div className="mx-2 flex w-fit items-center gap-1 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] p-1 sm:mx-4">
          {data.games!.map((g) => {
            const sel = g.gameId === data.gameId;
            return (
              <button
                key={g.gameId}
                onClick={() => setDashGameId(g.gameId)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${sel ? "bg-[var(--c-surface)] text-[var(--c-accent-ink)] shadow-[var(--c-shadow-sm)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
              >
                {shortGameName(g.gameName)}{g.isActive ? "" : " (retired)"}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] w-fit mx-2 sm:mx-4">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === t.key ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)] border border-[var(--c-line)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-3)] border border-transparent"}`}>
            <span>{t.icon}</span>{t.label}
            {t.key === "requests" && joinRequests.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold leading-none">
                {joinRequests.length}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="px-2 sm:px-4">
        {activeTab === "overview" && <OverviewTab data={data} stats={stats} />}
        {activeTab === "raids" && <RaidsTab data={data} />}
        {activeTab === "roster" && <RosterTab guildId={guildId} data={data} stats={stats} onUpdated={loadDashboard} />}
        {activeTab === "squads" && <SquadsTab guildId={guildId} />}
        {activeTab === "attendance" && <AttendanceTab guildId={guildId} />}
        {activeTab === "loot" && data && (
          <GuildLootImportsTab
            guildId={guildId}
            gameCode={data.guild.gameCode ?? null}
            members={data.members.map((m) => ({ id: m.characterId, name: m.characterName }))}
          />
        )}
        {activeTab === "ledger" && data && (
          <GuildLootLedgerTab
            guildId={guildId}
            gameCode={data.guild.gameCode ?? null}
          />
        )}
        {activeTab === "consumables" && data && (
          <GuildConsumablesTab
            guildId={guildId}
            gameId={data.gameId ?? null}
            members={data.members.map((m) => ({ id: m.characterId, name: m.characterName }))}
          />
        )}
        {activeTab === "requests" && (
          <RequestsTab
            guildId={guildId}
            requests={joinRequests}
            onChanged={() => { loadJoinRequests(); loadDashboard(); }}
          />
        )}
      </div>
    </div>
    </AvailColumnsContext.Provider>
  );
}

// ══════════════════════════════════════════════════════════
// OVERVIEW TAB
// ══════════════════════════════════════════════════════════

function OverviewTab({ data, stats }: { data: DashboardData; stats: any }) {
  const { raids, members } = data;
  const nextRaid = raids[0] || null;
  const nextSessionIso = nextRaid?.nextSession || nextRaid?.sessions?.[0]?.starts_at || null;

  return (
    <div className="space-y-5 animate-in fade-in duration-200">
      {/* ROW 1: Hero stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-cyan-500/15 bg-cyan-500/[0.04] p-4">
          <div className="text-xl mb-1">👥</div>
          <div className="text-3xl font-black text-[var(--c-ink)] tabular-nums">{stats.totalMembers}</div>
          <div className="text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--c-ink-4)] mt-1">Guild Members</div>
        </div>
        <div className="rounded-xl border border-violet-500/15 bg-violet-500/[0.04] p-4">
          <div className="text-xl mb-1">⚔️</div>
          <div className="text-3xl font-black text-[var(--c-ink)] tabular-nums">{stats.totalRaids}</div>
          <div className="text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--c-ink-4)] mt-1">Active Raids</div>
          {nextSessionIso && (
            <div className="text-[10px] text-[var(--c-ink-4)] mt-0.5">Next: {fmtCountdown(nextSessionIso).text}</div>
          )}
        </div>
        <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 flex items-center gap-4">
          <Ring pct={stats.rosterPct} color={stats.rosterPct >= 80 ? "#34d399" : stats.rosterPct >= 50 ? "#fbbf24" : "#f87171"}>
            <span className="text-sm font-black tabular-nums text-[var(--c-ink)]">{stats.rosterPct}%</span>
          </Ring>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--c-ink-4)]">Roster Health</div>
            <div className="text-xs text-[var(--c-ink-3)] mt-0.5">{stats.totalAssigned} of {stats.totalMembers} assigned</div>
          </div>
        </div>
        <Tip text="Shows the % of members who have confirmed their availability for the upcoming reset week. Confirmed means they actively verified their schedule.">
          <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 flex items-center gap-4 w-full cursor-help">
            <Ring pct={stats.confirmedPct} color={stats.confirmedPct >= 70 ? "#34d399" : stats.confirmedPct >= 40 ? "#fbbf24" : "#f87171"}>
              <span className="text-sm font-black tabular-nums text-[var(--c-ink)]">{stats.confirmedPct}%</span>
            </Ring>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--c-ink-4)]">Confirmed</div>
              <div className="text-xs text-[var(--c-ink-3)] mt-0.5">{stats.availConfirmed} of {stats.totalMembers}</div>
            </div>
          </div>
        </Tip>
      </div>

      {/* ROW 2: Full-width adaptive grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* COL 1: Raids */}
        <div className="xl:col-span-1 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-4)]">Raid Schedule</div>
          {raids.length === 0 ? (
            <div className="py-12 text-center rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)]">
              <div className="text-2xl mb-2 opacity-30">📅</div>
              <div className="text-xs text-[var(--c-ink-4)]">No upcoming raids</div>
            </div>
          ) : (
            <div className="space-y-2">
              {raids.map((r, i) => <RaidCard key={r.id} raid={r} isNext={i === 0} />)}
            </div>
          )}
          {(stats.willingRL > 0 || stats.willingLM > 0) && (
            <div className="flex gap-2 mt-2">
              <div className="flex-1 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] px-3 py-2 flex items-center gap-2">
                <span>🗡️</span>
                <div><div className="text-lg font-black text-[var(--c-ink)] tabular-nums">{stats.willingRL}</div><div className="text-[9px] text-[var(--c-ink-4)] uppercase">Raid Leads</div></div>
              </div>
              <div className="flex-1 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] px-3 py-2 flex items-center gap-2">
                <span>💰</span>
                <div><div className="text-lg font-black text-[var(--c-ink)] tabular-nums">{stats.willingLM}</div><div className="text-[9px] text-[var(--c-ink-4)] uppercase">Loot Masters</div></div>
              </div>
            </div>
          )}
          <BestRaidWindows members={members} resetRule={data.weekInfo?.resetRule} />
        </div>

        {/* COL 2: Availability + Weeks */}
        <div className="xl:col-span-1 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-4)]">Availability</div>
          <WeekCard weekInfo={data.weekInfo} stats={stats} />
          <SpecBreakdown specCounts={stats.specCounts} />
        </div>

        {/* COL 3: Composition */}
        <div className="xl:col-span-1 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-4)]">Composition</div>
          <ClassBreakdown classCounts={stats.classCounts} total={stats.totalMembers} />
          {/* Quick roster preview — top concerns */}
          <AlertsCard stats={stats} raids={raids} />
        </div>
      </div>
    </div>
  );
}

// ── BEST RAID WINDOWS ──────────────────────────────────────
// Collapses granular availability into one row per day: the time-of-day bracket
// + best contiguous 3-hour window and how many distinct raiders are free for the
// WHOLE window. Sorted most-available day first. (e.g. "Wednesday · Evening — 14 raiders · 19:30–22:30")
const FULL_DAY_NAME: Record<string, string> = {
  Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday",
  Sun: "Sunday", Mon: "Monday", Tue: "Tuesday",
};
const WINDOW_SLOTS = 6; // 6 × 30min = 3 hours

function slotToTime(s: number): string {
  const h = Math.floor(s / 2);
  const m = s % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
}
function bracketForStartSlot(s: number): string {
  const h = Math.floor(s / 2);
  if (h >= 5 && h < 12) return "Morning";
  if (h >= 12 && h < 17) return "Afternoon";
  if (h >= 17 && h < 23) return "Evening";
  return "Late Night";
}
const BRACKET_STYLE: Record<string, { tx: string; bd: string; bg: string }> = {
  Morning:     { tx: "#fbbf24", bd: "rgba(251,191,36,0.3)",  bg: "rgba(251,191,36,0.08)" },
  Afternoon:   { tx: "#38bdf8", bd: "rgba(56,189,248,0.3)",  bg: "rgba(56,189,248,0.08)" },
  Evening:     { tx: "#a78bfa", bd: "rgba(167,139,250,0.3)", bg: "rgba(167,139,250,0.08)" },
  "Late Night":{ tx: "#64748b", bd: "rgba(100,116,139,0.3)", bg: "rgba(100,116,139,0.08)" },
};

function BestRaidWindows({ members, resetRule }: { members: DashboardMember[]; resetRule?: ResetRule }) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  // Default to whichever week actually has data (prefer next, the week being planned).
  const anyNext = members.some(m => m.nextWeekBits && m.nextWeekBits.length >= 336);
  const [week, setWeek] = useState<"current" | "next">(anyNext ? "next" : "current");
  const [confirmedOnly, setConfirmedOnly] = useState(false);

  const { rows, totalRaiders } = useMemo(() => {
    // Anchor to the canonical client-computed reset Wednesday — NOT the server-serialized
    // weekInfo date. That arrives as a tz-shifted timestamp, and slice(0,10) can roll it back
    // a day, which shifts every day's availability by one (verified against live data).
    // With the guild's rule: for a US guild the EU default is a week behind
    // every Tuesday after reset.
    const weekIso = week === "next" ? getNextResetWeek(resetRule) : getCurrentResetWeek(resetRule);
    // Dedupe to distinct players (a user with multiple chars shares one availability).
    const userBits = new Map<string, string>();
    for (const m of members) {
      const bits = week === "next" ? m.nextWeekBits : m.currentWeekBits;
      const confirmed = week === "next" ? m.nextWeekConfirmed : m.currentWeekConfirmed;
      if (confirmedOnly && !confirmed) continue;
      if (bits && bits.length >= 336 && !userBits.has(m.userId)) userBits.set(m.userId, bits);
    }
    const bitsList = Array.from(userBits.values());

    const out = availDays.map((short, displayIdx) => {
      const dayMon0 = availDayLocalMon0[displayIdx];
      // The slot→bit mapping is the same for every member, so resolve it once per slot
      // (this is the costly luxon/timezone step), then just index each member's bitstring.
      const slotBit = new Array(48);
      for (let s = 0; s < 48; s++) slotBit[s] = localCellToBitIdx(weekIso, dayMon0, s, userTz);
      const count = new Array(48).fill(0);
      for (const bits of bitsList) {
        for (let s = 0; s < 48; s++) if (bits[slotBit[s]] === "1") count[s]++;
      }
      // Best 3h window = the start slot maximizing the MIN coverage across all 6 slots
      // (i.e. raiders free for the entire window). Tie-break on total coverage, then earlier start.
      let best = { min: -1, sum: -1, start: 0 };
      for (let s = 0; s + WINDOW_SLOTS <= 48; s++) {
        let mn = Infinity, sum = 0;
        for (let k = 0; k < WINDOW_SLOTS; k++) { const c = count[s + k]; if (c < mn) mn = c; sum += c; }
        if (mn > best.min || (mn === best.min && sum > best.sum)) best = { min: mn, sum, start: s };
      }
      return {
        short,
        fullDay: FULL_DAY_NAME[short] || short,
        bracket: bracketForStartSlot(best.start),
        count: Math.max(0, best.min),
        windowLabel: `${slotToTime(best.start)}–${slotToTime(best.start + WINDOW_SLOTS)}`,
      };
    });

    out.sort((a, b) => b.count - a.count);
    return { rows: out, totalRaiders: userBits.size };
  }, [members, week, userTz, confirmedOnly, resetRule]);

  const maxCount = rows[0]?.count || 0;

  return (
    <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-[var(--c-ink-4)]">Best Raid Windows</div>
          <div className="text-[10px] text-[var(--c-ink-4)] mt-0.5">Best 3-hour slot per day · {totalRaiders} raider{totalRaiders === 1 ? "" : "s"} with {confirmedOnly ? "confirmed " : ""}availability</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConfirmedOnly(v => !v)}
            title="Count only raiders who confirmed their availability for this week"
            className={`px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider border transition-all ${
              confirmedOnly ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25" : "bg-[var(--c-bg-veil)] text-[var(--c-ink-4)] border-[var(--c-line-soft)] hover:text-[var(--c-ink-3)]"
            }`}
          >
            {confirmedOnly ? "✓ " : ""}Confirmed only
          </button>
          <div className="flex items-center gap-1 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] p-0.5">
            {(["current", "next"] as const).map(w => (
              <button
                key={w}
                type="button"
                onClick={() => setWeek(w)}
                className={`px-2.5 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition-all ${
                  week === w ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/20" : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent"
                }`}
              >
                {w === "current" ? "This Week" : "Next Week"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {totalRaiders === 0 ? (
        <div className="py-8 text-center text-xs text-[var(--c-ink-4)] italic">No availability data for this week yet</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const st = BRACKET_STYLE[r.bracket] || BRACKET_STYLE["Late Night"];
            const pct = maxCount > 0 ? (r.count / maxCount) * 100 : 0;
            const dim = r.count === 0;
            return (
              <div key={r.short} className={`relative overflow-hidden rounded-lg border px-3 py-2 ${dim ? "border-[var(--c-line-soft)] bg-[var(--c-bg-veil)]" : "border-[var(--c-line)] bg-[var(--c-bg-veil)]"}`}>
                {!dim && <div className="absolute inset-y-0 left-0 rounded-lg" style={{ width: `${pct}%`, backgroundColor: st.bg }} />}
                <div className="relative flex items-center gap-3">
                  <span className={`text-[10px] font-black tabular-nums w-4 ${i === 0 && !dim ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-4)]"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${dim ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink)]"}`}>{r.fullDay}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: tc(st.tx), borderColor: st.bd, backgroundColor: st.bg, border: `1px solid ${st.bd}` }}>{r.bracket}</span>
                    </div>
                    <div className="text-[10px] text-[var(--c-ink-3)] tabular-nums mt-0.5">{dim ? "no common window" : r.windowLabel}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-lg font-black tabular-nums ${dim ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink)]"}`}>{r.count}</span>
                    <div className="text-[9px] text-[var(--c-ink-4)] uppercase -mt-1">raiders</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AlertsCard({ stats, raids }: { stats: any; raids: DashboardRaid[] }) {
  const alerts: { icon: string; text: string; color: string }[] = [];
  if (stats.availMissing > 0) alerts.push({ icon: "🔴", text: `${stats.availMissing} member${stats.availMissing > 1 ? "s" : ""} missing availability`, color: "text-rose-600/80 dark:text-rose-400/80" });
  if (stats.availStale > 0) alerts.push({ icon: "🟡", text: `${stats.availStale} member${stats.availStale > 1 ? "s have" : " has"} stale availability`, color: "text-amber-700/80 dark:text-amber-400/80" });
  raids.forEach(r => {
    if (!r.hasRaidLeader) alerts.push({ icon: "⚠️", text: `${r.name} needs a Raid Lead`, color: "text-amber-700/80 dark:text-amber-400/80" });
    if (!r.hasLootMaster) alerts.push({ icon: "⚠️", text: `${r.name} needs a Loot Master`, color: "text-amber-700/80 dark:text-amber-400/80" });
    if (r.raidSize && r.assignedCount < r.raidSize) {
      const needed = r.raidSize - r.assignedCount;
      alerts.push({ icon: "📋", text: `${r.name} needs ${needed} more player${needed > 1 ? "s" : ""}`, color: "text-[var(--c-ink-3)]" });
    }
  });
  if (stats.unrostered > 0 && stats.totalRaids > 0) alerts.push({ icon: "👤", text: `${stats.unrostered} unassigned member${stats.unrostered > 1 ? "s" : ""}`, color: "text-[var(--c-ink-3)]" });

  if (alerts.length === 0) return (
    <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-4 text-center">
      <div className="text-2xl mb-1">✅</div>
      <div className="text-xs text-emerald-700/80 dark:text-emerald-400/80 font-semibold">Everything looks good!</div>
    </div>
  );

  return (
    <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4 space-y-2">
      <div className="text-[10px] uppercase font-bold tracking-widest text-[var(--c-ink-4)]">Attention Needed</div>
      {alerts.slice(0, 6).map((a, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span className="flex-shrink-0">{a.icon}</span>
          <span className={a.color}>{a.text}</span>
        </div>
      ))}
      {alerts.length > 6 && <div className="text-[10px] text-[var(--c-ink-4)]">+{alerts.length - 6} more</div>}
    </div>
  );
}


// ══════════════════════════════════════════════════════════
// RAID CARD (Overview)
// ══════════════════════════════════════════════════════════

function RaidCard({ raid, isNext }: { raid: DashboardRaid; isNext: boolean }) {
  const userTz = useTimezone();
  const countdownIso = raid.nextSession || (raid.sessions && raid.sessions[0]?.starts_at) || null;
  const countdown = countdownIso ? fmtCountdown(countdownIso) : null;
  const scheduleLabel = dashboardRaidScheduleOneLine(raid, userTz);
  const fillPct = raid.raidSize ? Math.min(100, Math.round((raid.assignedCount / raid.raidSize) * 100)) : 0;
  const isFull = raid.raidSize ? raid.assignedCount >= raid.raidSize : false;

  return (
    <div className={`relative rounded-xl border overflow-hidden transition-all ${
      isNext ? "border-violet-500/25 bg-gradient-to-r from-violet-950/20 to-[var(--c-surface)]" : "border-[var(--c-line-soft)] bg-[var(--c-surface)] hover:border-[var(--c-line)]"
    }`}>
      {raid.raidImage && (
        <div className="absolute inset-0 opacity-[0.07]">
          <img src={raid.raidImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--c-surface)] to-transparent" />
        </div>
      )}
      <div className="relative p-3 flex items-center gap-3">
        {raid.raidImage && <img src={raid.raidImage} alt="" className="w-12 h-12 rounded-lg object-cover border border-[var(--c-line)] flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {isNext && <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-500/30">Next</span>}
            <span className="text-sm font-bold text-[var(--c-ink)] truncate">{raid.name}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[var(--c-ink-4)] min-w-0">
            {raid.raidType && <span className="text-[var(--c-ink-3)] shrink-0">{raid.raidType}</span>}
            {scheduleLabel && <span className="truncate">{scheduleLabel}</span>}
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            {raid.hasRaidLeader ? (
              <span className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /><span className="text-[var(--c-ink-4)]">RL</span>
                <span className="font-semibold" style={{ color: tc(cc(raid.raidLeaderClass)) }}>{raid.raidLeaderName}</span>
              </span>
            ) : <span className="text-[10px] text-amber-700/50 dark:text-amber-400/50">⚠ No RL</span>}
            {raid.hasLootMaster ? (
              <span className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="text-[var(--c-ink-4)]">LM</span>
                <span className="font-semibold" style={{ color: tc(cc(raid.lootMasterClass)) }}>{raid.lootMasterName}</span>
              </span>
            ) : <span className="text-[10px] text-amber-700/50 dark:text-amber-400/50">⚠ No LM</span>}
            {raid.srStatus && (
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                raid.srStatus.is_locked ? "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300"
              }`}>SR {raid.srStatus.is_locked ? "Locked" : "Open"} · {raid.srStatus.total_reserves}</span>
            )}
          </div>
        </div>
        {/* Right: ring + countdown */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          {countdown && (
            <span className={`text-[10px] font-bold tabular-nums ${countdown.urgent ? "text-amber-700 dark:text-amber-400" : "text-[var(--c-ink-4)]"}`}>{countdown.text}</span>
          )}
          <Ring pct={fillPct} size={44} stroke={2.5} color={isFull ? "#34d399" : fillPct > 60 ? "#a78bfa" : "#555"}>
            <span className={`text-[11px] font-black tabular-nums ${isFull ? "text-emerald-700 dark:text-emerald-400" : "text-[var(--c-ink-2)]"}`}>{raid.assignedCount}</span>
          </Ring>
          <span className="text-[9px] text-[var(--c-ink-4)]">of {raid.raidSize || "?"}</span>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════
// WEEK + AVAILABILITY CARD
// ══════════════════════════════════════════════════════════

function WeekCard({ weekInfo, stats }: { weekInfo: DashboardData["weekInfo"]; stats: any }) {
  const userTz = useTimezone();
  const bars = [
    { key: "confirmed", label: "Confirmed", desc: "Player set their availability AND pressed confirm for the upcoming week", count: stats.availConfirmed, color: "#34d399" },
    { key: "set", label: "Submitted", desc: "Player set availability for next week but hasn't confirmed it yet", count: stats.availSet, color: "#22d3ee" },
    { key: "stale", label: "Stale", desc: "Player hasn't set availability for next week — showing their previous week's data instead", count: stats.availStale, color: "#fbbf24" },
    { key: "missing", label: "Missing", desc: "Player has no availability data at all", count: stats.availMissing, color: "#f87171" },
  ];
  const total = stats.totalMembers;

  return (
    <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4 space-y-3">
      {/* Week labels */}
      <div className="flex gap-2">
        <div className="flex-1 rounded-lg bg-cyan-500/5 border border-cyan-500/10 px-3 py-2">
          <div className="text-[8px] uppercase font-black tracking-widest text-cyan-700/50 dark:text-cyan-400/50">This Week</div>
          <div className="text-xs font-semibold text-[var(--c-ink-3)] mt-0.5">{fmtWeek(weekInfo.currentWeek, userTz, weekInfo.resetWeekdayIso)}</div>
        </div>
        <div className="flex-1 rounded-lg bg-violet-500/5 border border-violet-500/10 px-3 py-2">
          <div className="text-[8px] uppercase font-black tracking-widest text-violet-600/50 dark:text-violet-400/50">Next Week</div>
          <div className="text-xs font-semibold text-[var(--c-ink-3)] mt-0.5">{fmtWeek(weekInfo.nextWeek, userTz, weekInfo.resetWeekdayIso)}</div>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="h-3 rounded-full bg-[var(--c-sunken)] overflow-hidden flex">
        {bars.map(b => {
          const pct = total > 0 ? (b.count / total) * 100 : 0;
          return pct > 0 ? <div key={b.key} className="h-full first:rounded-l-full last:rounded-r-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: b.color }} /> : null;
        })}
      </div>

      {/* Legend with tooltips */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {bars.map(b => (
          <Tip key={b.key} text={b.desc}>
            <div className="flex items-center gap-2 cursor-help w-full">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
              <span className="text-[11px] text-[var(--c-ink-3)] flex-1">{b.label}</span>
              <span className="text-[11px] font-bold tabular-nums text-[var(--c-ink-2)]">{b.count}</span>
            </div>
          </Tip>
        ))}
      </div>
    </div>
  );
}

function SpecBreakdown({ specCounts }: { specCounts: Record<string, { count: number; role: string; className: string; specLabel: string }> }) {
  const sorted = Object.entries(specCounts).sort(([, a], [, b]) => b.count - a.count);
  if (sorted.length === 0) return null;
  const max = sorted[0][1].count;

  return (
    <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4 space-y-2">
      <div className="text-[10px] uppercase font-bold tracking-widest text-[var(--c-ink-4)]">Spec Breakdown</div>
      <div className="space-y-[3px]">
        {sorted.map(([key, { count, role, className, specLabel }]) => {
          const style = rc(role);
          const pct = max > 0 ? (count / max) * 100 : 0;
          // Show "Class Spec" for disambiguation
          const displayName = specLabel || className || key;
          const classInitial = className ? className.charAt(0).toUpperCase() : "";
          return (
            <div key={key} className="flex items-center gap-2 group">
              <Tip text={`${className} – ${specLabel || fmtRole(role)}`}>
                <span className="text-[10px] text-[var(--c-ink-3)] w-28 truncate flex-shrink-0 cursor-help flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: cc(className) }} />
                  {displayName}
                </span>
              </Tip>
              <div className="flex-1 h-[18px] rounded bg-[var(--c-bg-veil)] overflow-hidden relative">
                <div className="h-full rounded transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: style.tx, opacity: 0.25 }} />
                <div className="absolute inset-y-0 left-2 flex items-center">
                  <span className="text-[10px] font-bold tabular-nums" style={{ color: tc(style.tx) }}>{count}</span>
                </div>
              </div>
              <span className="text-[10px] w-4 text-center flex-shrink-0" title={fmtRole(role)}>{ri(role)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClassBreakdown({ classCounts, total }: { classCounts: Record<string, number>; total: number }) {
  const sorted = Object.entries(classCounts).sort(([, a], [, b]) => b - a);
  if (sorted.length === 0) return null;
  return (
    <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4 space-y-3">
      <div className="text-[10px] uppercase font-bold tracking-widest text-[var(--c-ink-4)]">Classes</div>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map(([cls, count]) => {
          const color = cc(cls);
          return (
            <div key={cls} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border" style={{ borderColor: color + "25", backgroundColor: color + "08" }}>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-[11px] font-bold" style={{ color }}>{cls.charAt(0).toUpperCase() + cls.slice(1)}</span>
              <span className="text-[10px] font-bold tabular-nums text-[var(--c-ink-4)]">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════
// RAIDS TAB — Multi-select comparison
// ══════════════════════════════════════════════════════════

function RaidsTab({ data }: { data: DashboardData }) {
  const userTz = useTimezone();
  const { raids } = data;
  const [selected, setSelected] = useState<Set<string>>(new Set(raids[0] ? [raids[0].id] : []));

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { if (next.size >= 4) return prev; next.add(id); }
      return next;
    });
  };

  if (raids.length === 0) return (
    <div className="py-20 text-center rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] animate-in fade-in duration-200">
      <div className="text-4xl mb-3 opacity-30">⚔️</div><div className="text-sm text-[var(--c-ink-4)]">No active raids</div>
    </div>
  );

  const selectedRaids = raids.filter(r => selected.has(r.id));
  const comparing = selectedRaids.length > 1;

  return (
    <div className="animate-in fade-in duration-200 space-y-4">
      {/* Raid selector pills */}
      <div className="flex flex-wrap gap-2">
        {raids.map(raid => {
          const active = selected.has(raid.id);
          const fillPct = raid.raidSize ? Math.round((raid.assignedCount / raid.raidSize) * 100) : 0;
          return (
            <button key={raid.id} onClick={() => toggle(raid.id)} className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all ${
              active ? "bg-violet-500/10 border-violet-500/30 shadow-lg shadow-violet-500/5" : "bg-[var(--c-surface)] border-[var(--c-line-soft)] hover:border-[var(--c-line)]"
            }`}>
              {raid.raidImage && <img src={raid.raidImage} alt="" className="w-8 h-8 rounded-lg object-cover border border-[var(--c-line)]" />}
              <div className="text-left">
                <div className={`text-xs font-bold ${active ? "text-[var(--c-ink)]" : "text-[var(--c-ink-2)]"}`}>{raid.name}</div>
                <div className="text-[10px] text-[var(--c-ink-4)]">
                  {dashboardRaidScheduleOneLine(raid, userTz) ?? "No date"}
                  <span className="mx-1">·</span>
                  <span className="font-bold tabular-nums">{raid.assignedCount}/{raid.raidSize || "?"}</span>
                </div>
              </div>
              {active && <div className="w-2 h-2 rounded-full bg-violet-400 ml-1" />}
            </button>
          );
        })}
      </div>

      {/* Comparing banner */}
      {comparing && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
          <span className="text-violet-700 dark:text-violet-300 text-xs font-semibold">🔍 Comparing {selectedRaids.length} raids</span>
          <button onClick={() => setSelected(new Set([selectedRaids[0].id]))} className="ml-auto text-[10px] text-violet-600/60 dark:text-violet-400/60 hover:text-violet-700 dark:hover:text-violet-300 transition-colors">Clear comparison</button>
        </div>
      )}

      {/* Raid panels side by side */}
      <div className={`grid gap-4 ${
        selectedRaids.length === 1 ? "grid-cols-1" :
        selectedRaids.length === 2 ? "grid-cols-1 lg:grid-cols-2" :
        selectedRaids.length === 3 ? "grid-cols-1 lg:grid-cols-3" :
        "grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"
      }`}>
        {selectedRaids.map(raid => <RaidPanel key={raid.id} raid={raid} compact={selectedRaids.length > 2} />)}
      </div>
    </div>
  );
}

function RaidPanel({ raid, compact }: { raid: DashboardRaid; compact: boolean }) {
  const userTz = useTimezone();
  const fillPct = raid.raidSize ? Math.min(100, Math.round((raid.assignedCount / raid.raidSize) * 100)) : 0;
  const isFull = raid.raidSize ? raid.assignedCount >= raid.raidSize : false;
  const scheduleLabel = dashboardRaidScheduleOneLine(raid, userTz);

  const grouped = useMemo(() => {
    const g: Record<string, RaidAssignedChar[]> = { tank: [], healer: [], melee_dps: [], ranged_dps: [] };
    (raid.roster || []).forEach(c => {
      const role = (c.role_label || c.primary_role || "melee_dps").toLowerCase().replace(/\s+/g, "_");
      const key = role.includes("tank") ? "tank" : role.includes("heal") ? "healer" : role.includes("melee") ? "melee_dps" : "ranged_dps";
      g[key].push(c);
    });
    return g;
  }, [raid.roster]);

  const roles = [
    { key: "tank", label: "Tanks", icon: "🛡️" },
    { key: "healer", label: "Healers", icon: "💚" },
    { key: "melee_dps", label: "Melee", icon: "⚔️" },
    { key: "ranged_dps", label: "Ranged", icon: "🏹" },
  ];

  return (
    <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] overflow-hidden">
      {/* Header */}
      <div className="relative p-4 border-b border-[var(--c-line-soft)]">
        {raid.raidImage && (
          <div className="absolute inset-0 opacity-10">
            <img src={raid.raidImage} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--c-surface)] via-[var(--c-surface)] to-[var(--c-surface)]" />
          </div>
        )}
        <div className="relative flex items-center gap-3">
          {raid.raidImage && <img src={raid.raidImage} alt="" className={`${compact ? "w-10 h-10" : "w-14 h-14"} rounded-xl object-cover border border-[var(--c-line)]`} />}
          <div className="flex-1 min-w-0">
            <h3 className={`${compact ? "text-sm" : "text-base"} font-black text-[var(--c-ink)] truncate`}>{raid.name}</h3>
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-[var(--c-ink-4)] mt-0.5 min-w-0">
              {raid.raidType && <span className="text-[var(--c-ink-3)] shrink-0">{raid.raidType}</span>}
              {scheduleLabel && <span className="truncate">{scheduleLabel}</span>}
            </div>
            {!compact && (
              <div className="flex items-center gap-3 mt-1.5">
                {raid.hasRaidLeader ? (
                  <span className="flex items-center gap-1 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /><span className="font-semibold" style={{ color: tc(cc(raid.raidLeaderClass)) }}>{raid.raidLeaderName}</span></span>
                ) : <span className="text-[10px] text-amber-700/50 dark:text-amber-400/50">⚠ No RL</span>}
                {raid.hasLootMaster ? (
                  <span className="flex items-center gap-1 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="font-semibold" style={{ color: tc(cc(raid.lootMasterClass)) }}>{raid.lootMasterName}</span></span>
                ) : <span className="text-[10px] text-amber-700/50 dark:text-amber-400/50">⚠ No LM</span>}
              </div>
            )}
          </div>
          <Ring pct={fillPct} size={compact ? 40 : 52} stroke={2.5} color={isFull ? "#34d399" : fillPct > 60 ? "#a78bfa" : "#555"}>
            <span className={`${compact ? "text-[10px]" : "text-xs"} font-black tabular-nums ${isFull ? "text-emerald-700 dark:text-emerald-400" : "text-[var(--c-ink-2)]"}`}>{raid.assignedCount}</span>
          </Ring>
        </div>
      </div>

      {/* Roster by role */}
      {raid.roster && raid.roster.length > 0 ? (
        <div className={compact ? "p-3 space-y-2" : "grid grid-cols-2 lg:grid-cols-4 divide-x divide-[var(--c-line-soft)]"}>
          {roles.map(({ key, label, icon }) => {
            const chars = grouped[key] || [];
            const style = rc(key);
            return compact ? (
              <div key={key}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs">{icon}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: tc(style.tx) }}>{label} ({chars.length})</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {chars.map(c => (
                    <span key={c.character_id} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: tc(cc(c.class_name)), backgroundColor: cc(c.class_name) + "10" }}>
                      {c.character_name}
                    </span>
                  ))}
                  {chars.length === 0 && <span className="text-[9px] text-[var(--c-ink-4)] italic">—</span>}
                </div>
              </div>
            ) : (
              <div key={key} className="p-3">
                <div className="flex items-center gap-2 mb-2.5 pb-2 border-b" style={{ borderColor: style.bd }}>
                  <span className="text-sm">{icon}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: tc(style.tx) }}>{label}</span>
                  <span className="ml-auto text-xs font-bold tabular-nums" style={{ color: tc(style.tx) }}>{chars.length}</span>
                </div>
                <div className="space-y-0.5">
                  {chars.length === 0 && <div className="text-[9px] text-[var(--c-ink-4)] italic py-2 text-center">—</div>}
                  {chars.map(c => (
                    <div key={c.character_id} className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-[var(--c-bg-veil)] transition-colors">
                      <div className="w-1 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cc(c.class_name) }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold truncate" style={{ color: tc(cc(c.class_name)) }}>{c.character_name}</div>
                        <div className="text-[9px] text-[var(--c-ink-4)] truncate">{c.spec_labels?.length > 0 ? c.spec_labels.join(" / ") : c.primary_spec_label || "—"}</div>
                      </div>
                      {c.wcl_score != null && c.wcl_score > 0 && (
                        <span title={parseTooltip(c.wcl_score, c.wcl_parse)} className="text-[9px] font-bold tabular-nums px-1 py-px rounded" style={{ color: tc(parseColor(c.wcl_score)), backgroundColor: parseColor(c.wcl_score) + "12" }}>{c.wcl_score}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-8 text-center"><div className="text-xl mb-1 opacity-20">📋</div><div className="text-[10px] text-[var(--c-ink-4)]">No assignments yet</div></div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════
// ROSTER TAB — Sortable, paginated, all specs, availability
// ══════════════════════════════════════════════════════════

type SortKey = "name" | "spec" | "availability" | "raids" | "rating" | "parse";
type SortDir = "asc" | "desc";
const PAGE_SIZE = 20;

function RosterTab({ guildId, data, stats, onUpdated }: { guildId: string; data: DashboardData; stats: any; onUpdated: () => void }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "unrostered" | "missing_avail">("all");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<DashboardMember | null>(null);

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
    setPage(0);
  }, [sortKey]);

  const filtered = useMemo(() => {
    let list = [...data.members];
    if (filter === "unrostered") list = list.filter(m => !m.raidAssignments || m.raidAssignments.length === 0);
    else if (filter === "missing_avail") list = list.filter(m => m.availabilityStatus === "missing" || m.availabilityStatus === "stale");
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.characterName.toLowerCase().includes(q) || (m.displayName || "").toLowerCase().includes(q) ||
        (m.metadata?.primary_spec_label || "").toLowerCase().includes(q) || (m.metadata?.class_name || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let c = 0;
      switch (sortKey) {
        case "name": c = a.characterName.localeCompare(b.characterName); break;
        case "spec": c = (a.metadata?.primary_spec_label || "").localeCompare(b.metadata?.primary_spec_label || ""); break;
        case "availability": { const o: Record<string,number> = { confirmed: 0, set: 1, stale: 2, missing: 3 }; c = (o[a.availabilityStatus] ?? 4) - (o[b.availabilityStatus] ?? 4); break; }
        case "raids": c = (a.raidAssignments?.length || 0) - (b.raidAssignments?.length || 0); break;
        case "rating": c = (a.overallRating || 0) - (b.overallRating || 0); break;
        case "parse": c = (a.metadata?.wcl_perf?.score || 0) - (b.metadata?.wcl_perf?.score || 0); break;
      }
      return sortDir === "asc" ? c : -c;
    });
    return list;
  }, [data.members, filter, search, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const FILTERS: { key: typeof filter; label: string; count: number }[] = [
    { key: "all", label: "All", count: data.members.length },
    { key: "unrostered", label: "Unassigned", count: stats.unrostered },
    { key: "missing_avail", label: "Needs Avail", count: stats.availStale + stats.availMissing },
  ];

  return (
    <div className="space-y-3 animate-in fade-in duration-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => { setFilter(f.key); setPage(0); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${filter === f.key ? "bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]" : "bg-transparent border-[var(--c-line-soft)] text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)]"}`}>
              {f.label}
              <span className={`tabular-nums text-[10px] ${filter === f.key ? "text-[var(--c-ink-3)]" : "text-[var(--c-ink-4)]"}`}>{f.count}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--c-ink-4)] pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search name, class, spec..." className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg pl-8 pr-4 py-2 text-xs text-[var(--c-ink)] outline-none focus:border-[var(--c-line-strong)] w-56 placeholder:text-[var(--c-ink-4)]" />
        </div>
      </div>

      <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                <SH label="Character" sk="name" cur={sortKey} dir={sortDir} onSort={handleSort} />
                <SH label="Specs" sk="spec" cur={sortKey} dir={sortDir} onSort={handleSort} />
                <SH label="Availability" sk="availability" cur={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                <SH label="Raids" sk="raids" cur={sortKey} dir={sortDir} onSort={handleSort} />
                <SH label="Parse" sk="parse" cur={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                <SH label="Rating" sk="rating" cur={sortKey} dir={sortDir} onSort={handleSort} align="center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--c-line-soft)]">
              {paged.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-xs text-[var(--c-ink-4)] italic">{search ? "No matches." : "No members in this filter."}</td></tr>
              ) : paged.map(m => (
                <React.Fragment key={m.characterId}>
                  <RosterRow
                    member={m}
                    fields={data.fields}
                    weekInfo={data.weekInfo}
                    expanded={expandedId === m.characterId}
                    onToggle={() => setExpandedId(expandedId === m.characterId ? null : m.characterId)}
                    onEdit={() => setEditingMember(m)}
                  />
                  {expandedId === m.characterId && (m.currentWeekBits || m.nextWeekBits) && (
                    <tr><td colSpan={6} className="px-4 py-3 bg-[var(--c-bg-veil)]"><DualWeekAvailGrid currentWeekBits={m.currentWeekBits} nextWeekBits={m.nextWeekBits} weekInfo={data.weekInfo} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--c-line-soft)]">
            <span className="text-[10px] text-[var(--c-ink-4)]">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2.5 py-1 rounded text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken-2)] disabled:opacity-20 disabled:cursor-not-allowed transition-colors">‹ Prev</button>
              {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
                const p = totalPages <= 7 ? i : page <= 3 ? i : page >= totalPages - 4 ? totalPages - 7 + i : page - 3 + i;
                return (
                  <button key={p} onClick={() => setPage(p)} className={`w-7 h-7 rounded text-xs font-bold tabular-nums transition-colors ${p === page ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)]" : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)]"}`}>{p + 1}</button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-2.5 py-1 rounded text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken-2)] disabled:opacity-20 disabled:cursor-not-allowed transition-colors">Next ›</button>
            </div>
          </div>
        )}
      </div>

      {editingMember && (
        <RosterEditModal
          guildId={guildId}
          member={editingMember}
          fields={data.fields}
          weekInfo={data.weekInfo}
          onClose={() => setEditingMember(null)}
          onSaved={() => {
            setEditingMember(null);
            onUpdated();
          }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SQUADS TAB — Shows squads in the guild with members, characters, availability
// ══════════════════════════════════════════════════════════

type SquadMemberChar = { id: string; name: string; class_name: string; primary_spec_label: string | null; primary_role: string; spec_labels: string[]; wcl_score: number | null; wcl_parse?: WclParse | null; in_guild: boolean };
type SquadMember = { userId: string; displayName: string; joinedAt: string; characters: SquadMemberChar[]; currentWeekBits: string | null; currentWeekConfirmed: boolean; nextWeekBits: string | null; nextWeekConfirmed: boolean; isGuildMember: boolean };
type SquadData = { id: string; name: string; inviteCode: string; createdByUserId: string; creatorDisplayName: string; createdAt: string; members: SquadMember[] };
type SquadsResponse = { squads: SquadData[]; weekInfo?: { currentWeek: string; nextWeek: string; resetWeekdayIso?: number } };

const AVAIL_DAY_SHORT = ["W", "T", "F", "S", "S", "M", "T"];

function SquadsTab({ guildId }: { guildId: string }) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  const [data, setData] = useState<SquadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expandedSquadId, setExpandedSquadId] = useState<string | null>(null);
  const [showNextWeek, setShowNextWeek] = useState(true);
  const [visibleDays, setVisibleDays] = useState<number[]>([]);

  useEffect(() => {
    setLoading(true); setError(null);
    fetch(`/api/guilds/${guildId}/squads`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error("fetch_failed"); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [guildId]);

  const filteredSquads = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.squads;
    const q = search.toLowerCase();
    return data.squads.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.members.some(m =>
        (m.displayName || "").toLowerCase().includes(q) ||
        m.characters.some(c => c.name.toLowerCase().includes(q))
      )
    );
  }, [data, search]);

  // Character search result: find which squad(s) a character belongs to
  const characterSearchResults = useMemo(() => {
    if (!data || !search.trim()) return null;
    const q = search.toLowerCase();
    const results: { squad: SquadData; member: SquadMember; character: SquadMemberChar }[] = [];
    data.squads.forEach(s => {
      s.members.forEach(m => {
        m.characters.forEach(c => {
          if (c.name.toLowerCase().includes(q)) {
            results.push({ squad: s, member: m, character: c });
          }
        });
      });
    });
    return results.length > 0 ? results : null;
  }, [data, search]);

  const toggleDay = useCallback((dayIndex: number) => {
    setVisibleDays(prev =>
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex].sort((a, b) => a - b)
    );
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-[var(--c-line-soft)]" />
          <div className="absolute inset-0 rounded-full border-2 border-t-cyan-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
        </div>
        <span className="text-xs text-[var(--c-ink-4)] uppercase tracking-[0.2em]">Loading Squads</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="py-16 text-center">
      <div className="text-3xl mb-3 opacity-40">⚠️</div>
      <div className="text-sm text-[var(--c-ink-3)]">Failed to load squad data.</div>
    </div>
  );

  if (!data || data.squads.length === 0) return (
    <div className="py-16 text-center animate-in fade-in duration-200">
      <div className="text-4xl mb-3 opacity-30">🤝</div>
      <div className="text-sm text-[var(--c-ink-3)] font-medium">No squads found</div>
      <div className="text-xs text-[var(--c-ink-4)] mt-1">Guild members can create squads to be seated together in raids.</div>
    </div>
  );

  const squadsWeekInfo = data.weekInfo ?? { currentWeek: getCurrentResetWeek(), nextWeek: getNextResetWeek() };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-4)]">{data.squads.length} Squad{data.squads.length !== 1 ? "s" : ""}</span>
          <span className="text-[10px] text-[var(--c-ink-4)]">·</span>
          <span className="text-[10px] text-[var(--c-ink-4)]">{data.squads.reduce((a, s) => a + s.members.length, 0)} total members</span>
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--c-ink-4)] pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search squad, player, character..." className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg pl-8 pr-4 py-2 text-xs text-[var(--c-ink)] outline-none focus:border-[var(--c-line-strong)] w-64 placeholder:text-[var(--c-ink-4)]" />
        </div>
      </div>

      {/* Character search results banner */}
      {characterSearchResults && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-700/60 dark:text-cyan-400/60 mb-2">Character Search Results</div>
          <div className="space-y-1.5">
            {characterSearchResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="w-1 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cc(r.character.class_name) }} />
                <span className="font-bold" style={{ color: tc(cc(r.character.class_name)) }}>{r.character.name}</span>
                <span className="text-[var(--c-ink-4)]">·</span>
                <span className="text-[var(--c-ink-4)]">{r.character.primary_spec_label || "No spec"}</span>
                <span className="text-[var(--c-ink-4)]">→</span>
                <button onClick={() => { setExpandedSquadId(r.squad.id); }} className="text-cyan-700/80 dark:text-cyan-400/80 hover:text-cyan-700 dark:hover:text-cyan-300 font-semibold transition-colors">{r.squad.name}</button>
                <span className="text-[var(--c-ink-4)] text-[10px]">({r.member.displayName})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Availability controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Week toggle */}
        <div className="flex items-center gap-2 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] p-1">
          <button onClick={() => setShowNextWeek(false)} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${!showNextWeek ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/20" : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent"}`}>
            This Week
          </button>
          <button onClick={() => setShowNextWeek(true)} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${showNextWeek ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/20" : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent"}`}>
            Next Week
          </button>
        </div>
        <span className="text-[10px] text-[var(--c-ink-4)]">
          {showNextWeek ? fmtWeek(squadsWeekInfo.nextWeek, userTz, squadsWeekInfo.resetWeekdayIso) : fmtWeek(squadsWeekInfo.currentWeek, userTz, squadsWeekInfo.resetWeekdayIso)}
        </span>
        {/* Day selector pills (indices align with availDays: Wed → Tue) */}
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-[9px] text-[var(--c-ink-4)] uppercase tracking-wider mr-1">Days:</span>
          {availDays.map((day, i) => (
            <button key={day} onClick={() => toggleDay(i)} className={`w-7 h-7 rounded-lg text-[10px] font-bold transition-all ${visibleDays.includes(i) ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)] border border-[var(--c-line-strong)]" : "bg-[var(--c-bg-veil)] text-[var(--c-ink-4)] border border-[var(--c-line-soft)] hover:text-[var(--c-ink-3)] hover:border-[var(--c-line)]"}`}>
              {AVAIL_DAY_SHORT[i]}
            </button>
          ))}
          {visibleDays.length > 0 && (
            <button onClick={() => setVisibleDays([])} className="ml-1 px-2 h-7 rounded-lg text-[9px] font-bold text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] transition-all">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Squad cards */}
      <div className="space-y-3">
        {filteredSquads.length === 0 ? (
          <div className="py-12 text-center rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)]">
            <div className="text-2xl mb-2 opacity-20">🔍</div>
            <div className="text-xs text-[var(--c-ink-4)]">No squads match your search.</div>
          </div>
        ) : filteredSquads.map(squad => (
          <SquadCard
            key={squad.id}
            squad={squad}
            weekInfo={squadsWeekInfo}
            expanded={expandedSquadId === squad.id}
            onToggle={() => setExpandedSquadId(expandedSquadId === squad.id ? null : squad.id)}
            showNextWeek={showNextWeek}
            visibleDays={visibleDays}
          />
        ))}
      </div>
    </div>
  );
}

function SquadCard({ squad, weekInfo, expanded, onToggle, showNextWeek, visibleDays }: {
  squad: SquadData;
  weekInfo: { currentWeek: string; nextWeek: string; resetWeekdayIso?: number };
  expanded: boolean;
  onToggle: () => void;
  showNextWeek: boolean;
  visibleDays: number[];
}) {
  const guildMemberCount = squad.members.filter(m => m.isGuildMember).length;
  const externalCount = squad.members.length - guildMemberCount;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${expanded ? "border-[var(--c-line-strong)] bg-[var(--c-surface)]" : "border-[var(--c-line-soft)] bg-[var(--c-surface)] hover:border-[var(--c-line)]"}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none" onClick={onToggle}>
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border border-[var(--c-line)] flex items-center justify-center text-sm flex-shrink-0">
          🤝
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[var(--c-ink)] truncate">{squad.name}</span>
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[var(--c-sunken)] text-[var(--c-ink-4)] border border-[var(--c-line-soft)] tabular-nums">{squad.members.length} member{squad.members.length !== 1 ? "s" : ""}</span>
            {externalCount > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-700/60 dark:text-amber-400/60 border border-amber-500/15">{externalCount} external</span>
            )}
          </div>
          <div className="text-[10px] text-[var(--c-ink-4)] mt-0.5">
            Created by {squad.creatorDisplayName} · <span className="text-[var(--c-ink-4)]">{squad.inviteCode}</span>
          </div>
        </div>
        {/* Mini member avatars */}
        <div className="flex -space-x-1.5 mr-2">
          {squad.members.slice(0, 5).map((m, i) => {
            const mainChar = m.characters.find(c => c.in_guild) || m.characters[0];
            return (
              <div key={i} className="w-6 h-6 rounded-full border-2 border-[var(--c-line)] flex items-center justify-center text-[8px] font-black" style={{
                backgroundColor: mainChar ? (cc(mainChar.class_name) + "30") : "rgba(255,255,255,0.05)",
                color: tc(mainChar ? cc(mainChar.class_name) : "var(--c-ink-4)"),
              }}>{(m.displayName || "?")[0].toUpperCase()}</div>
            );
          })}
          {squad.members.length > 5 && (
            <div className="w-6 h-6 rounded-full border-2 border-[var(--c-line)] bg-[var(--c-sunken)] flex items-center justify-center text-[8px] font-bold text-[var(--c-ink-4)]">+{squad.members.length - 5}</div>
          )}
        </div>
        <svg className={`w-4 h-4 text-[var(--c-ink-4)] transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-[var(--c-line-soft)]">
          {squad.members.map(member => (
            <SquadMemberRow
              key={member.userId}
              member={member}
              weekCurrentIso={weekInfo.currentWeek}
              weekNextIso={weekInfo.nextWeek}
              showNextWeek={showNextWeek}
              visibleDays={visibleDays}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SquadMemberRow({
  member,
  weekCurrentIso,
  weekNextIso,
  showNextWeek,
  visibleDays,
}: {
  member: SquadMember;
  weekCurrentIso: string;
  weekNextIso: string;
  showNextWeek: boolean;
  visibleDays: number[];
}) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  const bits = showNextWeek ? member.nextWeekBits : member.currentWeekBits;
  const confirmed = showNextWeek ? member.nextWeekConfirmed : member.currentWeekConfirmed;
  const weekStartIso = String(showNextWeek ? weekNextIso : weekCurrentIso).slice(0, 10);
  const guildChars = member.characters.filter(c => c.in_guild);
  const otherChars = member.characters.filter(c => !c.in_guild);

  return (
    <div className={`px-4 py-3 border-b border-[var(--c-line-soft)] last:border-b-0 ${!member.isGuildMember ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        {/* Player info */}
        <div className="min-w-[140px] flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-[var(--c-ink)]">{member.displayName || "Unknown"}</span>
            {!member.isGuildMember && <span className="text-[8px] px-1 py-px rounded bg-amber-500/10 text-amber-700/60 dark:text-amber-400/60 border border-amber-500/15 font-bold">EXT</span>}
          </div>
          {/* Characters */}
          <div className="mt-1.5 space-y-0.5">
            {guildChars.map(c => (
              <div key={c.id} className="flex items-center gap-1.5">
                <div className="w-1 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cc(c.class_name) }} />
                <span className="text-[11px] font-semibold truncate" style={{ color: tc(cc(c.class_name)) }}>{c.name}</span>
                {c.primary_spec_label && (
                  <span className="text-[9px] text-[var(--c-ink-4)] truncate">{c.primary_spec_label}</span>
                )}
                {c.wcl_score != null && c.wcl_score > 0 && (
                  <span title={parseTooltip(c.wcl_score, c.wcl_parse)} className="text-[8px] font-bold tabular-nums px-1 rounded" style={{ color: tc(parseColor(c.wcl_score)), backgroundColor: parseColor(c.wcl_score) + "12" }}>{c.wcl_score}</span>
                )}
              </div>
            ))}
            {otherChars.length > 0 && (
              <div className="text-[9px] text-[var(--c-ink-4)] italic">{otherChars.length} other char{otherChars.length > 1 ? "s" : ""}</div>
            )}
          </div>
        </div>

        {/* Availability section */}
        <div className="flex-1 min-w-0">
          {bits && bits.length >= 336 ? (
            <div>
              {/* Mini heatmap always visible */}
              <div className="flex items-center gap-2 mb-1">
                <AvailMini
                  currentBits={member.currentWeekBits}
                  nextBits={member.nextWeekBits}
                  weekCurrentIso={weekCurrentIso}
                  weekNextIso={weekNextIso}
                  scheduleWeek={showNextWeek ? "next" : "current"}
                />
                <span className={`text-[9px] font-semibold ${confirmed ? "text-emerald-700/70 dark:text-emerald-400/70" : "text-[var(--c-ink-4)]"}`}>
                  {confirmed ? "Confirmed" : "Unconfirmed"}
                </span>
              </div>
              {/* Day detail rows for visible days */}
              {visibleDays.length > 0 && (
                <div className="space-y-px mt-1">
                  {visibleDays.map(dayIdx => {
                    const dayLocalMon0 = availDayLocalMon0[dayIdx];
                    const activeSlots = Array.from({ length: 48 }).filter((_, s) =>
                      availBitOn(bits, localCellToBitIdx(weekStartIso, dayLocalMon0, s, userTz))
                    ).length;
                    return (
                      <div key={dayIdx} className="flex items-center gap-1.5">
                        <span className={`w-7 text-[9px] font-bold text-right ${activeSlots > 0 ? "text-[var(--c-ink-3)]" : "text-[var(--c-ink-4)]"}`}>{availDays[dayIdx]}</span>
                        <div className="flex-1 h-[6px] bg-[var(--c-bg-veil)] rounded-sm overflow-hidden flex">
                          {Array.from({ length: 48 }).map((_, s) => {
                            const bi = localCellToBitIdx(weekStartIso, dayLocalMon0, s, userTz);
                            const on = availBitOn(bits, bi);
                            return (
                              <div
                                key={s}
                                className="flex-1"
                                style={{ backgroundColor: on ? "rgba(52,211,153,0.7)" : "transparent" }}
                              />
                            );
                          })}
                        </div>
                        <span className="text-[8px] text-[var(--c-ink-4)] tabular-nums w-8 text-right">{activeSlots > 0 ? `${Math.floor(activeSlots / 2)}h` : "—"}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-400/60" />
              <span className="text-[10px] text-rose-600/60 dark:text-rose-400/60 font-semibold">No availability</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function RosterEditModal({
  guildId,
  member,
  fields,
  weekInfo,
  onClose,
  onSaved,
}: {
  guildId: string;
  member: DashboardMember;
  fields: FieldGroup[];
  weekInfo: { currentWeek: string; nextWeek: string; resetWeekdayIso?: number };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { days: availDays, dayLocalMon0: availDayLocalMon0 } = useAvailColumns();
  const userTz = useTimezone();
  const { confirm } = useConfirm();
  const [name, setName] = useState(member.characterName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<"current" | "next">("next");
  const [currentBits, setCurrentBits] = useState<string>((member.currentWeekBits && member.currentWeekBits.length === 336) ? member.currentWeekBits : "0".repeat(336));
  const [nextBits, setNextBits] = useState<string>((member.nextWeekBits && member.nextWeekBits.length === 336) ? member.nextWeekBits : "0".repeat(336));
  const [auditEntries, setAuditEntries] = useState<Array<{ id: string; edited_at: string; edited_by_name: string }>>([]);
  const isDraggingRef = useRef(false);
  const dragValueRef = useRef<"0" | "1" | null>(null);

  const raidInterestGroup = useMemo(
    () => fields.find((g) => g.key === "raid_interest" || g.key === "raid_preferences") || null,
    [fields]
  );
  const [raidInterestIds, setRaidInterestIds] = useState<number[]>(() => {
    if (!raidInterestGroup) return [];
    return member.options
      .filter((o) => Number(o.group_id) === Number(raidInterestGroup.group_id))
      .map((o) => Number(o.option_id))
      .filter(Number.isFinite);
  });

  useEffect(() => {
    const stopDrag = () => {
      isDraggingRef.current = false;
      dragValueRef.current = null;
    };
    window.addEventListener("mouseup", stopDrag);
    return () => window.removeEventListener("mouseup", stopDrag);
  }, []);

  useEffect(() => {
    fetch(`/api/guilds/${guildId}/roster/${member.characterId}/audit?limit=5`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { entries: [] }))
      .then((d) => setAuditEntries(Array.isArray(d.entries) ? d.entries : []))
      .catch(() => setAuditEntries([]));
  }, [guildId, member.characterId]);

  const paintSlot = (idx: number, isStart: boolean) => {
    const setWeekBits = selectedWeek === "current" ? setCurrentBits : setNextBits;
    setWeekBits((prev) => {
      const arr = prev.split("") as ("0" | "1")[];
      if (isStart) {
        isDraggingRef.current = true;
        const nextValue: "0" | "1" = arr[idx] === "1" ? "0" : "1";
        dragValueRef.current = nextValue;
        arr[idx] = nextValue;
        return arr.join("");
      }
      if (isDraggingRef.current && dragValueRef.current && arr[idx] !== dragValueRef.current) {
        arr[idx] = dragValueRef.current;
        return arr.join("");
      }
      return prev;
    });
  };

  const handleSave = async (forceConfirm = false) => {
    if (forceConfirm) {
      const ok = await confirm({
        title: "Force Confirm Availability?",
        description: "This could result in rostering unavailable players, are you sure?",
        confirmText: "Force Confirm",
        cancelText: "Cancel",
        variant: "danger",
      });
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const targetWeekStart = String(selectedWeek === "current" ? weekInfo.currentWeek : weekInfo.nextWeek).slice(0, 10);
      const bits = selectedWeek === "current" ? currentBits : nextBits;
      const res = await fetch(`/api/guilds/${guildId}/roster/${member.characterId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          characterName: name.trim(),
          raidInterestOptionIds: raidInterestIds,
          weekStart: targetWeekStart,
          slots: bits,
          confirmAvailability: forceConfirm,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "save_failed");
      }
      onSaved();
    } catch (e: any) {
      setError(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const weekStartIso = String(selectedWeek === "current" ? weekInfo.currentWeek : weekInfo.nextWeek).slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)]">
        <div className="px-5 py-4 border-b border-[var(--c-line)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-700/70 dark:text-cyan-400/70 font-bold">Officer Edit</div>
            <div className="text-lg font-bold text-[var(--c-ink)]">{member.characterName}</div>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs border border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)]">Close</button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(90vh-130px)] space-y-5">
          {auditEntries.length > 0 && (
            <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--c-ink-3)] font-bold mb-2">Recent Officer Edits</div>
              <div className="space-y-1.5">
                {auditEntries.map((entry) => (
                  <div key={entry.id} className="text-xs text-[var(--c-ink-3)]">
                    Edited by <span className="text-cyan-700 dark:text-cyan-300 font-semibold">{entry.edited_by_name || "Unknown"}</span> on{" "}
                    <span className="text-[var(--c-ink-3)]">{new Date(entry.edited_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--c-ink-3)] font-bold block mb-1.5">Character Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              className="w-full max-w-sm bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-cyan-500/40"
            />
          </div>

          {raidInterestGroup && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[var(--c-ink-3)] font-bold block mb-2">Raid Interest</label>
              <div className="flex flex-wrap gap-2">
                {raidInterestGroup.options.map((opt) => {
                  const isOn = raidInterestIds.includes(Number(opt.id));
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        const id = Number(opt.id);
                        setRaidInterestIds((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                        isOn
                          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
                          : "bg-[var(--c-bg-veil)] border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] p-1 w-fit mb-3">
              <button
                type="button"
                onClick={() => setSelectedWeek("current")}
                className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
                  selectedWeek === "current"
                    ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/20"
                    : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent"
                }`}
              >
                This Week
              </button>
              <button
                type="button"
                onClick={() => setSelectedWeek("next")}
                className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
                  selectedWeek === "next"
                    ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/20"
                    : "text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent"
                }`}
              >
                Next Week
              </button>
            </div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-[var(--c-ink-3)] font-bold">
                Availability Grid ({selectedWeek === "current" ? "This Week" : "Next Week"})
              </label>
              <span className="text-[10px] text-[var(--c-ink-4)]">{fmtWeek(selectedWeek === "current" ? weekInfo.currentWeek : weekInfo.nextWeek, userTz, weekInfo.resetWeekdayIso)}</span>
            </div>
            <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-3 overflow-x-auto select-none">
              <div className="grid gap-1" style={{ gridTemplateColumns: "42px repeat(48, minmax(10px, 1fr))" }}>
                <div />
                {Array.from({ length: 48 }).map((_, i) => (
                  <div key={i} className="text-[8px] text-[var(--c-ink-4)] text-center">{i % 2 === 0 ? Math.floor(i / 2) : ""}</div>
                ))}
                {availDays.map((day, displayIdx) => {
                  const dayLocalMon0 = availDayLocalMon0[displayIdx];
                  return (
                    <React.Fragment key={day}>
                      <div className="text-[10px] text-[var(--c-ink-3)] font-bold self-center">{day}</div>
                      {Array.from({ length: 48 }).map((_, slotIdx) => {
                        const bitIdx = localCellToBitIdx(weekStartIso, dayLocalMon0, slotIdx, userTz);
                        const viewBits = selectedWeek === "current" ? currentBits : nextBits;
                        const on = viewBits[bitIdx] === "1";
                        return (
                          <button
                            key={`${day}-${slotIdx}`}
                            type="button"
                            onMouseDown={() => paintSlot(bitIdx, true)}
                            onMouseEnter={() => paintSlot(bitIdx, false)}
                            className={`h-3 rounded-[2px] border transition-colors ${
                              on ? "bg-cyan-400/80 border-cyan-300/90" : "bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] hover:bg-[var(--c-sunken)]"
                            }`}
                            title={`${day} ${Math.floor(slotIdx / 2)}:${slotIdx % 2 === 0 ? "00" : "30"}`}
                          />
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
          {error && <div className="text-xs text-rose-600 dark:text-rose-400">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-[var(--c-line)] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-xs border border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)]">Cancel</button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || name.trim().length < 2}
            className="px-4 py-2 rounded-lg text-xs font-bold bg-amber-500/20 border border-amber-500/35 text-amber-800 dark:text-amber-200 hover:bg-amber-500/30 disabled:opacity-50"
            title="Force confirms the selected week's availability"
          >
            {saving ? "Saving..." : "Force Confirm Availability"}
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving || name.trim().length < 2}
            className="px-4 py-2 rounded-lg text-xs font-bold bg-cyan-600/80 text-[var(--c-ink)] hover:bg-cyan-500 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SH({ label, sk, cur, dir, onSort, align }: { label: string; sk: SortKey; cur: SortKey; dir: SortDir; onSort: (k: SortKey) => void; align?: string }) {
  const active = sk === cur;
  return (
    <th onClick={() => onSort(sk)} className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest cursor-pointer select-none transition-colors hover:text-[var(--c-ink-3)] ${active ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-4)]"} ${align === "center" ? "text-center" : ""}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <svg className={`w-3 h-3 transition-transform ${dir === "desc" ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>}
      </span>
    </th>
  );
}

function RosterRow({
  member,
  fields,
  weekInfo,
  expanded,
  onToggle,
  onEdit,
}: {
  member: DashboardMember;
  fields: FieldGroup[];
  weekInfo: { currentWeek: string; nextWeek: string; resetWeekdayIso?: number };
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) {
  const cn = member.metadata?.class_name || "";
  const specLabel = member.metadata?.primary_spec_label || "";
  const allSpecs = member.metadata?.spec_labels || [];
  const role = member.metadata?.primary_role || "";
  const wclScore = member.metadata?.wcl_perf?.score ?? null;
  const roleStyle = rc(role);

  const cwConfirmed = member.currentWeekConfirmed;
  const nwConfirmed = member.nextWeekConfirmed;

  return (
    <tr className={`group transition-colors cursor-pointer ${expanded ? "bg-[var(--c-bg-veil)]" : "hover:bg-[var(--c-bg-veil)]"}`} onClick={onToggle}>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-1 h-7 rounded-full flex-shrink-0" style={{ backgroundColor: cc(cn) }} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold truncate" style={{ color: tc(cc(cn)) }}>{member.characterName}</span>
              {member.isOfficer && <span className="text-[8px]">⭐</span>}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] px-1.5 py-0.5 rounded border border-cyan-500/25 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-500/20"
                title="Edit member"
              >
                Edit
              </button>
            </div>
            <div className="text-[10px] text-[var(--c-ink-4)] truncate">{member.displayName || "—"}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {allSpecs.length > 0 ? allSpecs.map((s: string, i: number) => {
            const isPrimary = s === specLabel;
            return (
              <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${isPrimary ? "" : "opacity-50"}`} style={{
                backgroundColor: isPrimary ? roleStyle.bg : "transparent",
                borderColor: isPrimary ? roleStyle.bd : "rgba(255,255,255,0.06)",
                color: tc(isPrimary ? roleStyle.tx : "var(--c-ink-4)"),
              }}>
                {isPrimary && <span className="text-[9px]">{ri(role)}</span>}
                {s}
              </span>
            );
          }) : specLabel ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border" style={{
              backgroundColor: roleStyle.bg, borderColor: roleStyle.bd, color: tc(roleStyle.tx),
            }}>
              <span className="text-[9px]">{ri(role)}</span>{specLabel}
            </span>
          ) : (
            <span className="text-[10px] text-[var(--c-ink-4)] italic">{cn ? fmtRole(role) : "—"}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 text-center">
        <div className="inline-flex items-center gap-2">
          <AvailMini
            currentBits={member.currentWeekBits}
            nextBits={member.nextWeekBits}
            weekCurrentIso={weekInfo.currentWeek}
            weekNextIso={weekInfo.nextWeek}
          />
          <div className="flex items-center gap-1.5">
            <Tip text={`Current Week: ${cwConfirmed ? "Confirmed" : "Unconfirmed"}`}>
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border cursor-help ${cwConfirmed ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-700 dark:text-cyan-300" : "bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]"}`}>{cwConfirmed ? "✓" : "·"}</div>
            </Tip>
            <Tip text={`Next Week: ${nwConfirmed ? "Confirmed" : "Unconfirmed"}`}>
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border cursor-help ${nwConfirmed ? "bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-300" : "bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]"}`}>{nwConfirmed ? "✓" : "·"}</div>
            </Tip>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        {member.raidAssignments.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {member.raidAssignments.map((ra, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/10 border border-violet-500/15 text-violet-700 dark:text-violet-300 truncate max-w-[120px]">{ra.raid_name}</span>
            ))}
          </div>
        ) : <span className="text-[10px] text-[var(--c-ink-4)] italic">Unassigned</span>}
      </td>
      <td className="px-4 py-2.5 text-center">
        {wclScore != null && wclScore > 0 ? (
          <span className="text-sm font-black tabular-nums px-1.5 py-0.5 rounded" style={{ color: tc(parseColor(wclScore)), backgroundColor: parseColor(wclScore) + "10" }}>{wclScore}</span>
        ) : <span className="text-[10px] text-[var(--c-ink-4)]">—</span>}
      </td>
      <td className="px-4 py-2.5 text-center">
        {member.overallRating !== null ? (
          <div className="flex items-center justify-center gap-1">
            <span className={`text-sm font-bold tabular-nums ${member.overallRating >= 4 ? "text-emerald-700 dark:text-emerald-400" : member.overallRating >= 3 ? "text-[var(--c-ink-2)]" : "text-amber-700 dark:text-amber-400"}`}>{member.overallRating.toFixed(1)}</span>
            {member.hasLowRatings && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
          </div>
        ) : <span className="text-[10px] text-[var(--c-ink-4)]">—</span>}
      </td>
    </tr>
  );
}
// ══════════════════════════════════════════════════════════
// ATTENDANCE TAB
// ══════════════════════════════════════════════════════════
// Rows = players (one per user_id, aggregating characters).
// Columns = reset weeks (Wed→Wed UTC, newest left).
// Cell = one square per raid instance that ran that week, filled if the player
// was on the final roster of ANY run of that instance that week (so 6 Kara runs
// in one week collapse into 1 Kara square).
// Attendance signal is "on guild_raid_assignment when the session started" —
// assumes officers kept rosters accurate.

type AttendanceCharacter = {
  id: string;
  name: string;
  className: string | null;
  primaryRole: string | null;
  primarySpecLabel: string | null;
};

type AttendancePlayer = {
  userId: string;
  displayName: string | null;
  joinedAt: string | null;
  characters: AttendanceCharacter[];
};

type AttendanceRaidInstance = {
  id: number;
  label: string;
  imageUrl: string | null;
};

type AttendanceRow = {
  userId: string;
  characterId: string;
  characterName: string;
  className: string | null;
  weekStart: string;
  raidOptionId: number;
  guildRaidId: string;
  raidName: string;
  sessionStartsAt: string;
  roleLabel: string | null;
};

type RosterEventFeedRow = {
  id: string;
  action: string;
  reasonCode: string | null;
  countsAgainst: boolean;
  occurredAt: string;
  hoursBeforeStart: number | null;
  note: string | null;
  guildRaidId: string;
  raidName: string | null;
  userId: string | null;
  characterName: string | null;
  className: string | null;
  actorDisplayName: string | null;
};

type ReliabilityRow = {
  userId: string;
  strikes: number;
  benched: number;
  leftRaid: number;
  declined: number;
};

type AttendanceResponse = {
  weeks: string[];
  raidInstances: AttendanceRaidInstance[];
  weekInstances: Record<string, number[]>;
  players: AttendancePlayer[];
  attendance: AttendanceRow[];
  rosterEvents?: RosterEventFeedRow[];
  reliability?: ReliabilityRow[];
  weeksBack: number;
};

const ROSTER_REASON_LABELS: Record<string, string> = {
  bench: "Benched",
  no_show: "No-show",
  late: "Late",
  asked_out: "Asked out",
  replaced: "Replaced",
  schedule_change: "Rescheduled",
  behavior: "Behavior",
  performance: "Performance",
  other: "Other",
  unspecified: "Unspecified",
};

function formatWeekHeader(iso: string) {
  const s = String(iso || "").trim();
  if (!s) return "—";
  const forParse = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s;
  const d = new Date(forParse);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function AttendanceTab({ guildId }: { guildId: string }) {
  const [data, setData] = useState<AttendanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeksBack, setWeeksBack] = useState<number>(8);
  const [search, setSearch] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [instanceFilter, setInstanceFilter] = useState<Set<number> | null>(null);
  const [sortMode, setSortMode] = useState<"attendance" | "name">("attendance");
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    setLoading(true); setError(null);
    fetch(`/api/guilds/${guildId}/attendance?weeks=${weeksBack}`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error("fetch_failed"); return r.json(); })
      .then((d: AttendanceResponse) => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message || "fetch_failed"); setLoading(false); });
  }, [guildId, weeksBack]);

  // Lookup: `${userId}|${weekStart}|${raidOptionId}` → rows
  const attendanceLookup = useMemo(() => {
    const m = new Map<string, AttendanceRow[]>();
    if (!data) return m;
    data.attendance.forEach(row => {
      const key = `${row.userId}|${row.weekStart}|${row.raidOptionId}`;
      const arr = m.get(key);
      if (arr) arr.push(row); else m.set(key, [row]);
    });
    return m;
  }, [data]);

  // Per-character lookup (for expanded sub-rows)
  const charAttendanceLookup = useMemo(() => {
    const m = new Map<string, AttendanceRow[]>();
    if (!data) return m;
    data.attendance.forEach(row => {
      const key = `${row.characterId}|${row.weekStart}|${row.raidOptionId}`;
      const arr = m.get(key);
      if (arr) arr.push(row); else m.set(key, [row]);
    });
    return m;
  }, [data]);

  // Sorted per-week instance IDs: ensures Kara/Gruul/Mag always appear in the same
  // position in every cell across every row, so the grid is visually parseable.
  // Uses the global order from data.raidInstances (backend already sorts by label).
  const sortedWeekInstances = useMemo(() => {
    if (!data) return {} as Record<string, number[]>;
    const globalOrder = data.raidInstances.map(i => i.id);
    const out: Record<string, number[]> = {};
    Object.entries(data.weekInstances).forEach(([week, ids]) => {
      const idSet = new Set(ids);
      out[week] = globalOrder.filter(id => idSet.has(id));
    });
    return out;
  }, [data]);

  // Per-player reliability aggregates (roster removal events) over the window.
  const reliabilityLookup = useMemo(() => {
    const m = new Map<string, ReliabilityRow>();
    (data?.reliability || []).forEach(r => m.set(r.userId, r));
    return m;
  }, [data]);

  // Per-player attendance % over the window. Denominator skips pre-join weeks
  // and respects the instance filter.
  const playerStats = useMemo(() => {
    const stats = new Map<string, { attended: number; possible: number; pct: number; runs: number }>();
    if (!data) return stats;
    data.players.forEach(p => {
      const joinedDate = p.joinedAt ? new Date(p.joinedAt).toISOString().slice(0, 10) : null;
      let attended = 0;
      let possible = 0;
      let runs = 0; // over-counted: every character + every run of an instance counts separately
      data.weeks.forEach(weekStart => {
        if (joinedDate && weekStart < joinedDate) return;
        const instancesThisWeek = data.weekInstances[weekStart] || [];
        instancesThisWeek.forEach(instanceId => {
          if (instanceFilter && !instanceFilter.has(instanceId)) return;
          possible++;
          const cellRows = attendanceLookup.get(`${p.userId}|${weekStart}|${instanceId}`) || [];
          if (cellRows.length > 0) attended++;
          runs += new Set(cellRows.map(r => `${r.characterId}|${r.guildRaidId}`)).size;
        });
      });
      const pct = possible === 0 ? 0 : Math.round((attended / possible) * 100);
      stats.set(p.userId, { attended, possible, pct, runs });
    });
    return stats;
  }, [data, attendanceLookup, instanceFilter]);

  const displayedPlayers = useMemo(() => {
    if (!data) return [];
    let list = data.players.slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.displayName || "").toLowerCase().includes(q) ||
        p.characters.some(c => c.name.toLowerCase().includes(q))
      );
    }
    if (sortMode === "attendance") {
      list.sort((a, b) => {
        const aS = playerStats.get(a.userId);
        const bS = playerStats.get(b.userId);
        const aPct = aS && aS.possible > 0 ? aS.pct : -1;
        const bPct = bS && bS.possible > 0 ? bS.pct : -1;
        if (bPct !== aPct) return bPct - aPct;
        return (a.displayName || "").localeCompare(b.displayName || "");
      });
    } else {
      list.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    }
    return list;
  }, [data, search, sortMode, playerStats]);

  const toggleExpand = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleInstanceFilter = (instanceId: number) => {
    setInstanceFilter(prev => {
      if (!prev) return new Set([instanceId]);
      const next = new Set(prev);
      if (next.has(instanceId)) next.delete(instanceId); else next.add(instanceId);
      if (next.size === 0) return null;
      return next;
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-[var(--c-line-soft)]" />
          <div className="absolute inset-0 rounded-full border-2 border-t-cyan-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
        </div>
        <span className="text-xs text-[var(--c-ink-4)] uppercase tracking-[0.2em]">Loading Attendance</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="py-16 text-center">
      <div className="text-3xl mb-3 opacity-40">⚠️</div>
      <div className="text-sm text-[var(--c-ink-3)]">Failed to load attendance.</div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top controls */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--c-ink-4)]">Window:</span>
          {[4, 8, 12, 26].map(w => (
            <button
              key={w}
              onClick={() => setWeeksBack(w)}
              className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                weeksBack === w
                  ? "bg-cyan-500/20 text-cyan-800 dark:text-cyan-200 border border-cyan-500/30"
                  : "text-[var(--c-ink-3)] border border-[var(--c-line)] hover:text-[var(--c-ink-2)] hover:border-[var(--c-line-strong)]"
              }`}
            >
              {w}w
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search player or character…"
            className="px-3 py-1.5 rounded-md text-xs bg-[var(--c-sunken)] border border-[var(--c-line)] text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:outline-none focus:border-[var(--c-line-strong)] w-56"
          />
          <select
            value={sortMode}
            onChange={e => setSortMode(e.target.value as "attendance" | "name")}
            className="px-2.5 py-1.5 rounded-md text-xs bg-[var(--c-surface)] border border-[var(--c-line)] text-[var(--c-ink-2)] focus:outline-none"
          >
            <option value="attendance" className="bg-[var(--c-sunken)] text-[var(--c-ink)]">Sort: Attendance %</option>
            <option value="name" className="bg-[var(--c-sunken)] text-[var(--c-ink)]">Sort: Name</option>
          </select>
        </div>
      </div>

      {/* Instance filter pills */}
      {data.raidInstances.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--c-ink-4)]">Instances:</span>
          <button
            onClick={() => setInstanceFilter(null)}
            className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
              !instanceFilter
                ? "bg-violet-500/20 text-violet-800 dark:text-violet-200 border border-violet-500/30"
                : "text-[var(--c-ink-3)] border border-[var(--c-line)] hover:text-[var(--c-ink-2)] hover:border-[var(--c-line-strong)]"
            }`}
          >
            All
          </button>
          {data.raidInstances.map(inst => {
            const active = instanceFilter?.has(inst.id) ?? false;
            return (
              <button
                key={inst.id}
                onClick={() => toggleInstanceFilter(inst.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                  active
                    ? "bg-violet-500/20 text-violet-800 dark:text-violet-200 border border-violet-500/30"
                    : "text-[var(--c-ink-3)] border border-[var(--c-line)] hover:text-[var(--c-ink-2)] hover:border-[var(--c-line-strong)]"
                }`}
              >
                {inst.imageUrl && <img src={inst.imageUrl} alt="" className="w-4 h-4 rounded-sm" />}
                {inst.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {data.weeks.length === 0 ? (
        <div className="py-16 text-center rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)]">
          <div className="text-3xl mb-3 opacity-30">📅</div>
          <div className="text-sm text-[var(--c-ink-3)] font-medium">No raid history</div>
          <div className="text-xs text-[var(--c-ink-4)] mt-1">No past raids in the last {weeksBack} weeks.</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)]">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--c-line-soft)]">
                <th className="sticky left-0 z-10 bg-[var(--c-surface)] px-3 py-2 text-left min-w-[240px]">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--c-ink-4)]">Player</span>
                </th>
                <th className="px-2 py-2 text-center w-16">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--c-ink-4)]">Att %</span>
                </th>
                {data.weeks.map(weekStart => {
                  const instancesThisWeek = (sortedWeekInstances[weekStart] || [])
                    .filter(id => !instanceFilter || instanceFilter.has(id));
                  return (
                    <th key={weekStart} className="px-2 py-2 text-center border-l border-[var(--c-line-soft)]">
                      <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[var(--c-ink-3)]">
                        {formatWeekHeader(weekStart)}
                      </div>
                      <div className="text-[9px] text-[var(--c-ink-4)] mt-0.5 tabular-nums">
                        {instancesThisWeek.length} inst
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayedPlayers.map(player => (
                <AttendancePlayerRow
                  key={player.userId}
                  player={player}
                  weeks={data.weeks}
                  weekInstances={sortedWeekInstances}
                  instanceFilter={instanceFilter}
                  raidInstances={data.raidInstances}
                  attendanceLookup={attendanceLookup}
                  charAttendanceLookup={charAttendanceLookup}
                  stats={playerStats.get(player.userId)}
                  reliability={reliabilityLookup.get(player.userId)}
                  isExpanded={expandedUsers.has(player.userId)}
                  onToggle={() => toggleExpand(player.userId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap text-[10px] text-[var(--c-ink-3)]">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-emerald-500/60 border border-[var(--c-line-strong)]" />
          <span>Attended (class-colored)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm border border-[var(--c-line-strong)]" />
          <span>Raid ran, not on roster</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[var(--c-bg-veil)] border border-dashed border-[var(--c-line)]" />
          <span>Before joining guild</span>
        </div>
        <div className="flex-1 text-right text-[var(--c-ink-4)] text-[9px]">
          Based on final roster assignments · resets Wed 00:00 UTC
        </div>
      </div>

      {/* Recent roster changes (removal tracking feed) */}
      {data.rosterEvents && data.rosterEvents.length > 0 && (
        <div className="rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--c-ink-4)]">Recent roster changes</span>
            <span className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink-3)] tabular-nums">{data.rosterEvents.length}</span>
            <Tip text="Every removal, self-leave, and declined invite across this guild's raids in the selected window. Red = counts against attendance. Set reasons when removing players in the Planner.">
              <span className="text-[10px] text-[var(--c-ink-4)] cursor-help">ⓘ</span>
            </Tip>
          </div>
          <div className="space-y-1">
            {(showAllEvents ? data.rosterEvents : data.rosterEvents.slice(0, 10)).map(ev => {
              const when = ev.occurredAt
                ? new Date(ev.occurredAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                : "";
              const actionLabel =
                ev.action === "removed" ? (ev.actorDisplayName ? `removed by ${ev.actorDisplayName}` : "removed") :
                ev.action === "left" ? (ev.hoursBeforeStart !== null && ev.hoursBeforeStart < 24 ? "left (short notice)" : "left the raid") :
                ev.action === "declined_invite" ? "declined the invite" :
                ev.action === "kicked_from_guild" ? "removed from guild" : ev.action;
              return (
                <div key={ev.id} className="flex items-center gap-2 rounded-lg bg-[var(--c-bg-veil)] px-2.5 py-1.5 text-xs">
                  <span className="whitespace-nowrap text-[10px] text-[var(--c-ink-4)] tabular-nums w-24 flex-shrink-0">{when}</span>
                  <span className="font-bold flex-shrink-0" style={{ color: tc(CC[(ev.className || "").toLowerCase()] || "#e5e7eb") }}>
                    {ev.characterName || "Unknown"}
                  </span>
                  <span className="text-[var(--c-ink-3)]">{actionLabel}</span>
                  {ev.raidName && <span className="truncate text-[var(--c-ink-4)]">· {ev.raidName}</span>}
                  {ev.note && (
                    <Tip text={ev.note}>
                      <span className="text-[var(--c-ink-4)] cursor-help flex-shrink-0">✎</span>
                    </Tip>
                  )}
                  <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                    {ev.action === "removed" && ev.reasonCode && ev.reasonCode !== "unspecified" && (
                      <span className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink-2)]">
                        {ROSTER_REASON_LABELS[ev.reasonCode] || ev.reasonCode}
                      </span>
                    )}
                    {ev.countsAgainst && (
                      <span className="rounded-full bg-red-500/15 border border-red-500/30 px-2 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-300">counts</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {data.rosterEvents.length > 10 && (
            <button
              onClick={() => setShowAllEvents(s => !s)}
              className="mt-2 text-[11px] font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] transition-colors"
            >
              {showAllEvents ? "Show less" : `Show all ${data.rosterEvents.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AttendancePlayerRow({
  player,
  weeks,
  weekInstances,
  instanceFilter,
  raidInstances,
  attendanceLookup,
  charAttendanceLookup,
  stats,
  reliability,
  isExpanded,
  onToggle,
}: {
  player: AttendancePlayer;
  weeks: string[];
  weekInstances: Record<string, number[]>;
  instanceFilter: Set<number> | null;
  raidInstances: AttendanceRaidInstance[];
  attendanceLookup: Map<string, AttendanceRow[]>;
  charAttendanceLookup: Map<string, AttendanceRow[]>;
  stats: { attended: number; possible: number; pct: number; runs: number } | undefined;
  reliability: ReliabilityRow | undefined;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const joinedDate = player.joinedAt ? new Date(player.joinedAt).toISOString().slice(0, 10) : null;
  const instanceLabelMap = useMemo(() => {
    const m = new Map<number, string>();
    raidInstances.forEach(i => m.set(i.id, i.label));
    return m;
  }, [raidInstances]);

  return (
    <>
      {/* Main player row */}
      <tr className="border-b border-[var(--c-line-soft)] hover:bg-[var(--c-bg-veil)] transition-colors">
        <td className="sticky left-0 z-10 bg-[var(--c-surface)] px-3 py-2 border-r border-[var(--c-line-soft)]">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggle}
              className="w-5 h-5 flex items-center justify-center text-[var(--c-ink-4)] hover:text-[var(--c-ink-2)] transition-colors flex-shrink-0"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <svg className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="text-sm font-bold text-[var(--c-ink)] truncate">{player.displayName || "(unknown)"}</div>
                {reliability && reliability.strikes > 0 && (
                  <Tip text={`${reliability.strikes} removal${reliability.strikes > 1 ? "s" : ""} that count against attendance in this window (no-shows, late drops, behavior)`}>
                    <span className="flex-shrink-0 rounded-full bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 text-[9px] font-black text-red-700 dark:text-red-300 tabular-nums cursor-help">
                      {reliability.strikes} ✕
                    </span>
                  </Tip>
                )}
                {reliability && reliability.benched > 0 && (
                  <Tip text={`Benched ${reliability.benched} time${reliability.benched > 1 ? "s" : ""} in this window (doesn't count against them)`}>
                    <span className="flex-shrink-0 rounded-full bg-sky-500/10 border border-sky-500/25 px-1.5 py-0.5 text-[9px] font-black text-sky-700 dark:text-sky-300 tabular-nums cursor-help">
                      {reliability.benched} B
                    </span>
                  </Tip>
                )}
              </div>
              {player.characters.length > 0 && (
                <div className="flex items-center gap-1 mt-0.5">
                  {player.characters.slice(0, 6).map(c => (
                    <Tip key={c.id} text={`${c.name}${c.className ? ` (${c.className})` : ""}`}>
                      <div
                        className="w-4 h-4 rounded-sm border border-[var(--c-line)] flex-shrink-0"
                        style={{ backgroundColor: CC[(c.className || "").toLowerCase()] || "#555" }}
                      />
                    </Tip>
                  ))}
                  {player.characters.length > 6 && (
                    <span className="text-[10px] text-[var(--c-ink-4)] ml-0.5">+{player.characters.length - 6}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-2 py-2 text-center">
          {stats && stats.possible > 0 ? (
            <div className="flex flex-col items-center">
              <div className={`text-sm font-black tabular-nums ${
                stats.pct >= 80 ? "text-emerald-700 dark:text-emerald-400" :
                stats.pct >= 50 ? "text-amber-700 dark:text-amber-400" :
                "text-red-600 dark:text-red-400"
              }`}>
                {stats.pct}%
              </div>
              <Tip text={`${stats.runs} total raid runs attended (counts every character + every run) out of ${stats.possible} raid-weeks. The % above is how many weeks you showed up at all.`}>
                <div className="text-[9px] text-[var(--c-ink-4)] tabular-nums cursor-help">{stats.runs}/{stats.possible}</div>
              </Tip>
            </div>
          ) : (
            <span className="text-[10px] text-[var(--c-ink-4)]">—</span>
          )}
        </td>
        {weeks.map(weekStart => {
          const instancesThisWeek = (weekInstances[weekStart] || [])
            .filter(id => !instanceFilter || instanceFilter.has(id));
          const isPreJoin = joinedDate != null && weekStart < joinedDate;

          return (
            <td key={weekStart} className="px-2 py-2 border-l border-[var(--c-line-soft)]">
              <div className="flex items-center justify-center gap-1 flex-wrap">
                {instancesThisWeek.length === 0 ? (
                  <span className="text-[10px] text-[var(--c-ink-4)]">—</span>
                ) : instancesThisWeek.map(instanceId => {
                  const key = `${player.userId}|${weekStart}|${instanceId}`;
                  const rows = attendanceLookup.get(key) || [];
                  const attended = rows.length > 0;
                  const instanceLabel = instanceLabelMap.get(instanceId) || "?";

                  if (isPreJoin) {
                    return (
                      <Tip key={instanceId} text={`${instanceLabel} · before joining guild`}>
                        <div className="w-3 h-3 rounded-sm bg-[var(--c-bg-veil)] border border-dashed border-[var(--c-line)]" />
                      </Tip>
                    );
                  }

                  if (attended) {
                    const cls = (rows[0].className || "").toLowerCase();
                    const color = CC[cls] || "#34d399";
                    const charNames = Array.from(new Set(rows.map(r => r.characterName))).join(", ");
                    const runCount = new Set(rows.map(r => r.guildRaidId)).size;
                    return (
                      <Tip key={instanceId} text={`${instanceLabel} · ${charNames}${runCount > 1 ? ` · ${runCount} runs` : ""}`}>
                        <div
                          className="w-3 h-3 rounded-sm border border-[var(--c-line-strong)]"
                          style={{ backgroundColor: color }}
                        />
                      </Tip>
                    );
                  }

                  return (
                    <Tip key={instanceId} text={`${instanceLabel} · not on roster`}>
                      <div className="w-3 h-3 rounded-sm border border-[var(--c-line-strong)] bg-transparent" />
                    </Tip>
                  );
                })}
              </div>
            </td>
          );
        })}
      </tr>

      {/* Expanded: per-character sub-rows */}
      {isExpanded && player.characters.map(char => {
        const possible = stats?.possible ?? 0;
        // This character's contribution to the player's over-counted total (same denominator).
        let charRuns = 0;
        weeks.forEach(weekStart => {
          if (joinedDate && weekStart < joinedDate) return;
          const insts = (weekInstances[weekStart] || []).filter(id => !instanceFilter || instanceFilter.has(id));
          insts.forEach(instanceId => {
            charRuns += new Set((charAttendanceLookup.get(`${char.id}|${weekStart}|${instanceId}`) || []).map(r => r.guildRaidId)).size;
          });
        });
        return (
        <tr key={char.id} className="border-b border-[var(--c-line-soft)] bg-[var(--c-bg-veil)]">
          <td className="sticky left-0 z-10 bg-[var(--c-surface)] px-3 py-1.5 border-r border-[var(--c-line-soft)]">
            <div className="flex items-center gap-2 pl-6">
              <div
                className="w-3 h-3 rounded-sm border border-[var(--c-line)] flex-shrink-0"
                style={{ backgroundColor: CC[(char.className || "").toLowerCase()] || "#555" }}
              />
              <span className="text-xs text-[var(--c-ink-2)] truncate">{char.name}</span>
              {char.className && <span className="text-[10px] text-[var(--c-ink-4)]">{char.className}</span>}
            </div>
          </td>
          <td className="px-2 py-1.5 text-center">
            {possible > 0 ? (
              <span className="text-[10px] text-[var(--c-ink-3)] tabular-nums">{charRuns}/{possible}</span>
            ) : (
              <span className="text-[10px] text-[var(--c-ink-4)]">—</span>
            )}
          </td>
          {weeks.map(weekStart => {
            const instancesThisWeek = (weekInstances[weekStart] || [])
              .filter(id => !instanceFilter || instanceFilter.has(id));
            const isPreJoin = joinedDate != null && weekStart < joinedDate;

            return (
              <td key={weekStart} className="px-2 py-1.5 border-l border-[var(--c-line-soft)]">
                <div className="flex items-center justify-center gap-1 flex-wrap">
                  {instancesThisWeek.map(instanceId => {
                    const key = `${char.id}|${weekStart}|${instanceId}`;
                    const rows = charAttendanceLookup.get(key) || [];
                    const attended = rows.length > 0;
                    const instanceLabel = instanceLabelMap.get(instanceId) || "?";

                    if (isPreJoin) {
                      return (
                        <Tip key={instanceId} text={`${instanceLabel} · before joining guild`}>
                          <div className="w-2.5 h-2.5 rounded-sm bg-[var(--c-bg-veil)] border border-dashed border-[var(--c-line)]" />
                        </Tip>
                      );
                    }

                    if (attended) {
                      const color = CC[(char.className || "").toLowerCase()] || "#34d399";
                      const runCount = new Set(rows.map(r => r.guildRaidId)).size;
                      return (
                        <Tip key={instanceId} text={`${instanceLabel} · ${char.name}${runCount > 1 ? ` · ${runCount} runs` : ""}`}>
                          <div
                            className="w-2.5 h-2.5 rounded-sm border border-[var(--c-line-strong)]"
                            style={{ backgroundColor: color }}
                          />
                        </Tip>
                      );
                    }

                    return (
                      <Tip key={instanceId} text={`${instanceLabel} · not on ${char.name}`}>
                        <div className="w-2.5 h-2.5 rounded-sm border border-[var(--c-line)]" />
                      </Tip>
                    );
                  })}
                </div>
              </td>
            );
          })}
        </tr>
        );
      })}
    </>
  );
}
// ══════════════════════════════════════════════════════════
// REQUESTS TAB — pending join applications from the Discord flow
// ══════════════════════════════════════════════════════════

function RequestsTab({ guildId, requests, onChanged }: {
  guildId: string;
  requests: JoinRequestRow[];
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  // Request currently being declined, plus the officer's optional reason. The
  // note is DM'd to the applicant, so declining is a two-step action rather
  // than a single click.
  const [declining, setDeclining] = useState<{ req: JoinRequestRow; note: string; reasonKey: string | null } | null>(null);
  const [reasons, setReasons] = useState<{ key: string; label: string }[]>([]);
  const NOTE_MAX = 300;

  // Preset list is owned by the server so the applicant-facing wording can
  // never drift from what actually gets sent.
  useEffect(() => {
    let alive = true;
    fetch("/join-decline-reasons", { credentials: "include" })
      .then(r => (r.ok ? r.json() : { reasons: [] }))
      .then(d => { if (alive) setReasons(Array.isArray(d.reasons) ? d.reasons : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const decide = async (
    req: JoinRequestRow,
    action: "approve" | "reject",
    payload?: { reasonKey?: string | null; note?: string },
  ) => {
    setBusyId(req.id);
    setFeedback(null);
    try {
      const res = await fetch(`/guilds/join-requests/${req.id}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(action === "reject" ? (payload || {}) : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data.error === "already_in_guild" ? "That character already joined a guild with an invite code, so the application was closed." :
          data.error === "character_gone" ? "That character no longer exists, so the application was closed." :
          data.error === "realm_mismatch" ? (data.message || "Realm mismatch.") :
          data.error === "faction_mismatch" ? (data.message || "Faction mismatch.") :
          data.error === "not_pending" ? "This application was already decided." :
          data.error === "note_too_long" ? `Your note is too long (max ${data.max} characters).` :
          data.error === "invalid_reason" ? "Pick one of the listed reasons." :
          "Something went wrong. Try again.";
        setFeedback(msg);
      } else {
        setDeclining(null);
      }
      onChanged();
    } catch {
      setFeedback("Something went wrong. Try again.");
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const waitingSince = (iso: string) => {
    const rel = DateTime.fromISO(iso).toRelative();
    return rel || "";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-wider">Pending requests</h3>
        <span className="text-xs text-[var(--c-ink-4)]">From the guild directory or Discord (/ralli join)</span>
      </div>

      {feedback && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          {feedback}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] p-10 text-center">
          <div className="text-3xl mb-3 opacity-60">📭</div>
          <div className="text-sm text-[var(--c-ink-3)]">No pending applications.</div>
          <div className="text-xs text-[var(--c-ink-4)] mt-2">
            Players in your Discord can apply with <span className="text-[var(--c-ink-3)] font-mono">/ralli join</span> or the
            "Join this guild" button on the weekly availability post.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map(req => {
            const color = cc(req.className);
            return (
              <div key={req.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-4 py-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-black border flex-shrink-0"
                  style={{ color, borderColor: `${color}44`, background: `${color}14` }}
                >
                  {(req.characterName || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-bold truncate" style={{ color }}>{req.characterName || "(deleted)"}</span>
                    <span className="text-xs text-[var(--c-ink-3)]">
                      {req.className}
                      {req.specLabels.length > 0 && <span className="text-[var(--c-ink-4)]"> · {req.specLabels.join(", ")}</span>}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
                      req.source === "web"
                        ? "bg-cyan-500/10 border-cyan-400/25 text-cyan-700 dark:text-cyan-300"
                        : "bg-[#5865F2]/10 border-[#5865F2]/30 text-[#4752C4] dark:text-[#98a0f5]"
                    }`}>
                      {req.source === "web" ? "Directory" : "Discord"}
                    </span>
                  </div>
                  <div className="text-[11px] text-[var(--c-ink-4)]">
                    {req.displayName || "Unknown player"} · applied {waitingSince(req.requestedAt)}
                  </div>
                  {req.currentGuildName && (
                    <div className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                      Currently in <span className="font-semibold">{req.currentGuildName}</span> · accepting moves them here
                    </div>
                  )}
                </div>
                {req.wclScore != null && req.wclScore > 0 && (
                  req.wclUrl ? (
                    <a
                      href={req.wclUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View Warcraft Logs profile"
                      className="flex flex-col items-center flex-shrink-0 group/wcl"
                    >
                      <span className="text-lg font-black tabular-nums leading-none group-hover/wcl:underline" style={{ color: tc(parseColor(req.wclScore)) }}>{req.wclScore}</span>
                      <span className="text-[8px] uppercase tracking-wider text-[var(--c-ink-4)] group-hover/wcl:text-[var(--c-ink-3)] flex items-center gap-0.5">
                        parse
                        <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                      </span>
                    </a>
                  ) : (
                    <div className="flex flex-col items-center flex-shrink-0">
                      <span className="text-lg font-black tabular-nums leading-none" style={{ color: tc(parseColor(req.wclScore)) }}>{req.wclScore}</span>
                      <span className="text-[8px] uppercase tracking-wider text-[var(--c-ink-4)]">parse</span>
                    </div>
                  )
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => decide(req, "approve")}
                    disabled={busyId === req.id}
                    className="px-4 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-700 dark:text-emerald-300 text-xs font-bold transition-colors disabled:opacity-40"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => { setFeedback(null); setDeclining({ req, note: "", reasonKey: null }); }}
                    disabled={busyId === req.id}
                    className="px-4 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-400/25 text-red-700 dark:text-red-300 text-xs font-bold transition-colors disabled:opacity-40"
                  >
                    Decline
                  </button>
                </div>
              {declining?.req.id === req.id && (
                <div className="w-full mt-1 rounded-xl border border-red-400/25 bg-red-500/[0.06] p-3.5 space-y-2.5">
                  <div className="text-xs font-bold text-red-800 dark:text-red-200">
                    Decline {req.characterName || "this application"}?
                  </div>
                  <div className="text-[11px] text-[var(--c-ink-3)]">
                    {req.characterName || "The character"} stays on {req.displayName || "the player"}'s roster,
                    they just won't join the guild.
                  </div>

                  {/* Applicant-facing: preset only */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-1.5">
                      Reason sent to them
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {reasons.map(r => {
                        const sel = declining.reasonKey === r.key;
                        return (
                          <button
                            key={r.key}
                            onClick={() => setDeclining({ ...declining, reasonKey: sel ? null : r.key })}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
                              sel ? "bg-red-500/20 border-red-400/45 text-red-800 dark:text-red-200"
                                  : "bg-[var(--c-bg-veil)] border-[var(--c-line)] text-[var(--c-ink-3)] hover:border-[var(--c-line-strong)]"
                            }`}
                          >
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-1.5 text-[10px] text-[var(--c-ink-4)]">
                      Pick one, or none to decline without giving a reason. Rallify sends the wording,
                      so declines stay polite whoever clicks the button.
                    </div>
                  </div>

                  {/* Officer-facing: never leaves the guild */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-1.5">
                      Private note <span className="text-[var(--c-ink-4)] normal-case font-normal">· your records only, never sent</span>
                    </div>
                    <textarea
                      value={declining.note}
                      onChange={(e) => setDeclining({ ...declining, note: e.target.value })}
                      rows={2}
                      maxLength={NOTE_MAX}
                      placeholder="Optional, e.g. applied twice this month, parses looked off"
                      className="w-full px-3 py-2 rounded-lg bg-[var(--c-surface)] border border-[var(--c-line)] focus:border-[var(--c-line-strong)] focus:outline-none text-xs text-[var(--c-ink)] placeholder-white/25 resize-none transition-colors"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-[10px] text-[var(--c-ink-4)]">
                      {declining.note.length}/{NOTE_MAX}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setDeclining(null)}
                        className="px-3 py-1.5 rounded-lg bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] border border-[var(--c-line)] text-[var(--c-ink-3)] text-xs font-bold transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => decide(req, "reject", {
                          reasonKey: declining.reasonKey,
                          note: declining.note.trim() || undefined,
                        })}
                        disabled={busyId === req.id}
                        className="px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-800 dark:text-red-200 text-xs font-bold transition-colors disabled:opacity-40"
                      >
                        {busyId === req.id ? "Declining..." : "Send decline"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      <DiscordRecruitScan guildId={guildId} />
    </div>
  );
}

// ── Onboard (Discord role scan) ──────────────────────────────
// Officer picks a Discord role on the linked server, the bot returns everyone
// holding it, and we bucket them against Rallify accounts. Exact discord_id
// matches are fact; fuzzy name hits are advisory and always shown for review.
// Owners can then nudge the "Not on Rallify" bucket with ONE channel post.

type DiscordRole = { id: string; name: string; color: string | null; position: number };
// Limits come from the server so the composer can never disagree with what the
// API will accept. See NUDGE_* in auth/discord_bot.js for the Discord ceilings.
type NudgeConfig = {
  canNudge: boolean;
  maxMessageLength: number;
  maxMentions: number;
  mentionsPerPost: number;
  defaultMessage: string;
};
type ScanMember = {
  discord_id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  match?: {
    displayName?: string | null;
    characters?: string[];
    characterName?: string;
    score?: number;
  };
};
type ScanResult = {
  roleId: string;
  scanned: number;
  totalWithRole: number;
  capped: boolean;
  buckets: {
    in_guild: ScanMember[];
    on_rallify: ScanMember[];
    fuzzy: ScanMember[];
    not_signed_up: ScanMember[];
  };
};

const SCAN_GROUPS: {
  key: keyof ScanResult["buckets"];
  label: string;
  hint: string;
  dotClass: string;
  countClass: string;
}[] = [
  {
    key: "not_signed_up",
    label: "Not on Rallify",
    hint: "Your recruit targets: no account found for these Discord members",
    dotClass: "bg-rose-400",
    countClass: "bg-rose-500/15 border-rose-400/30 text-rose-700 dark:text-rose-300",
  },
  {
    key: "fuzzy",
    label: "Possible matches, review",
    hint: "Name looks like someone on your roster (maybe an alt Discord account)",
    dotClass: "bg-amber-400",
    countClass: "bg-amber-500/15 border-amber-400/30 text-amber-700 dark:text-amber-300",
  },
  {
    key: "on_rallify",
    label: "On Rallify, not in this guild",
    hint: "Have an account already, just not a member here",
    dotClass: "bg-sky-400",
    countClass: "bg-sky-500/15 border-sky-400/30 text-sky-700 dark:text-sky-300",
  },
  {
    key: "in_guild",
    label: "On Rallify, in this guild",
    hint: "All set, nothing to do",
    dotClass: "bg-emerald-400",
    countClass: "bg-emerald-500/15 border-emerald-400/30 text-emerald-700 dark:text-emerald-300",
  },
];

// One result bucket with its own little search. The filter matches Discord
// names and any matched Rallify identity, so "naeg" finds the alt account row.
// Rows lay out in responsive columns because a raider role is routinely 60+
// people and a single stack turns the tab into a mile of scrolling. Buckets
// with nothing to action ("in this guild") start folded for the same reason.
function ScanBucket({ group, members, selectable, selected, onToggle, atCap, startFolded }: {
  group: (typeof SCAN_GROUPS)[number];
  members: ScanMember[];
  selectable?: boolean;
  selected?: Set<string>;
  onToggle?: (discordId: string) => void;
  atCap?: boolean;
  startFolded?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(!startFolded);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter(m =>
        m.display_name.toLowerCase().includes(q) ||
        m.username.toLowerCase().includes(q) ||
        (m.match?.characterName || "").toLowerCase().includes(q) ||
        (m.match?.displayName || "").toLowerCase().includes(q) ||
        (m.match?.characters || []).some(c => c.toLowerCase().includes(q)))
    : members;

  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-3">
      <div className="flex items-center gap-2 mb-1">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 min-w-0 text-left group/fold"
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${group.dotClass}`} />
          <span className="text-xs font-bold text-[var(--c-ink-2)] group-hover/fold:text-[var(--c-ink)] truncate">{group.label}</span>
          <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-bold leading-none flex-shrink-0 ${group.countClass}`}>
            {q ? `${filtered.length}/${members.length}` : members.length}
          </span>
          <span className={`text-[var(--c-ink-4)] group-hover/fold:text-[var(--c-ink-3)] text-[9px] flex-shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}>
            ▼
          </span>
        </button>
        {open && members.length > 5 && (
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search…"
            className="ml-auto w-36 px-2.5 py-1 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] focus:border-[var(--c-line-strong)] focus:outline-none text-[11px] text-[var(--c-ink)] placeholder-white/25 transition-colors"
          />
        )}
      </div>
      {open && <div className="text-[10px] text-[var(--c-ink-4)] mb-2 ml-4">{group.hint}</div>}
      {!open ? null : members.length === 0 ? (
        <div className="text-[11px] text-[var(--c-ink-4)] ml-4">Nobody here.</div>
      ) : filtered.length === 0 ? (
        <div className="text-[11px] text-[var(--c-ink-4)] ml-4">No names match "{query.trim()}".</div>
      ) : (
        <div className="grid gap-1 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map(m => {
            const isSel = !!selected?.has(m.discord_id);
            // Once the mention cap is hit, unselected rows go inert rather than
            // silently dropping people at send time.
            const blocked = selectable && !isSel && atCap;
            return (
            <div
              key={m.discord_id}
              onClick={selectable && !blocked ? () => onToggle?.(m.discord_id) : undefined}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors ${
                selectable && !blocked ? "cursor-pointer" : ""
              } ${
                isSel
                  ? "bg-rose-500/10 hover:bg-rose-500/15"
                  : blocked
                  ? "bg-[var(--c-bg-veil)] opacity-35"
                  : "bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)]"
              }`}
            >
              {selectable && (
                <span
                  className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center text-[9px] font-bold ${
                    isSel
                      ? "bg-rose-500/70 border-rose-400/60 text-[var(--c-ink)]"
                      : "border-[var(--c-line-strong)]"
                  }`}
                >
                  {isSel ? "✓" : ""}
                </span>
              )}
              <img
                src={m.avatar_url}
                alt=""
                className="w-6 h-6 rounded-full bg-[var(--c-surface)] flex-shrink-0"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
              />
              {/* Stacked, not side by side: a column is ~380px wide, so the
                  matched-identity line needs its own row to stay readable. */}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <span className="text-xs font-semibold text-[var(--c-ink)] truncate">{m.display_name}</span>
                  <span className="text-[10px] text-[var(--c-ink-4)] truncate">@{m.username}</span>
                </div>
                {m.match && (
                  <div className="text-[10px] text-[var(--c-ink-3)] truncate">
                    {group.key === "fuzzy"
                      ? `Looks like ${m.match.characterName} (${m.match.score}% name match)`
                      : m.match.characters && m.match.characters.length > 0
                      ? m.match.characters.join(", ")
                      : m.match.displayName || ""}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiscordRecruitScan({ guildId }: { guildId: string }) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [roles, setRoles] = useState<DiscordRole[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [nudgeCfg, setNudgeCfg] = useState<NudgeConfig | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [nudgeMsg, setNudgeMsg] = useState("");
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [nudgeConfirm, setNudgeConfirm] = useState(false);
  const [nudgeError, setNudgeError] = useState<string | null>(null);
  const [nudgeDone, setNudgeDone] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/guilds/${guildId}/discord-roles`, { credentials: "include" })
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok) {
          setRolesError(d.error === "bot_not_ready"
            ? "The Discord bot is offline right now. Try again in a minute."
            : "Couldn't load roles from your Discord server.");
          return;
        }
        setConfigured(!!d.configured);
        setRoles(Array.isArray(d.roles) ? d.roles : []);
        if (d.nudge) setNudgeCfg(d.nudge as NudgeConfig);
      })
      .catch(() => { if (alive) setRolesError("Couldn't load roles from your Discord server."); });
    return () => { alive = false; };
  }, [guildId]);

  const scan = async () => {
    if (!selectedRole || scanning) return;
    setScanning(true);
    setScanError(null);
    setResult(null);
    setSelected(new Set());
    setNudgeError(null);
    setNudgeDone(null);
    setNudgeConfirm(false);
    try {
      const res = await fetch(`/api/guilds/${guildId}/discord-role-scan`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleId: selectedRole }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setScanError(
          d.error === "members_intent_missing"
            ? "The bot can't list server members yet. Enable the Server Members Intent for the bot in the Discord Developer Portal, then restart it."
            : d.error === "bot_not_ready"
            ? "The Discord bot is offline right now. Try again in a minute."
            : "Scan failed. Try again."
        );
      } else {
        setResult(d as ScanResult);
      }
    } catch {
      setScanError("Scan failed. Try again.");
    } finally {
      setScanning(false);
    }
  };

  const targets = result?.buckets.not_signed_up ?? [];
  const maxMentions = nudgeCfg?.maxMentions ?? 100;
  const canCompose = !!nudgeCfg?.canNudge && targets.length > 0;
  const atCap = selected.size >= maxMentions;
  const msgLen = nudgeMsg.trim().length;
  const overLength = !!nudgeCfg && msgLen > nudgeCfg.maxMessageLength;

  const toggle = (id: string) => {
    setNudgeConfirm(false);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < maxMentions) next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setNudgeConfirm(false);
    // Fills up to the cap in list order rather than refusing outright.
    setSelected(new Set(targets.slice(0, maxMentions).map(m => m.discord_id)));
  };

  const sendNudge = async () => {
    if (!nudgeConfirm) { setNudgeConfirm(true); return; }
    if (selected.size === 0 || nudgeBusy || overLength) return;
    setNudgeBusy(true);
    setNudgeError(null);
    setNudgeDone(null);
    try {
      const res = await fetch(`/api/guilds/${guildId}/discord-nudge`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discordIds: Array.from(selected), message: nudgeMsg.trim() || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNudgeError(
          d.error === "rate_limited"
            ? `You've sent too many nudges recently. Try again in about ${Math.ceil((d.waitSeconds || 3600) / 60)} minutes.`
            : d.error === "owner_only"
            ? "Only the guild owner can post a nudge."
            : d.error === "no_channel_linked"
            ? "No Discord channel is linked yet. Set one in guild settings first."
            : d.error === "all_already_registered"
            ? "Everyone you picked already has a Rallify account. Re-scan to refresh."
            : d.error === "no_recipients_in_server"
            ? "None of those people are still in your Discord server."
            : d.error === "message_too_long"
            ? `Message is too long (max ${d.max} characters).`
            : d.error === "too_many_recipients"
            ? `You can only nudge ${d.max} people at once.`
            : d.error === "bot_not_ready"
            ? "The Discord bot is offline right now. Try again in a minute."
            : "Couldn't post the nudge. Try again."
        );
      } else {
        const extras = [
          d.skippedRegistered ? `${d.skippedRegistered} already signed up` : null,
          d.skippedNotInServer ? `${d.skippedNotInServer} no longer in the server` : null,
        ].filter(Boolean);
        setNudgeDone(
          `Posted to your Discord channel: ${d.mentioned} mentioned across ${d.posts} post${d.posts === 1 ? "" : "s"}.` +
          (extras.length ? ` Skipped ${extras.join(" and ")}.` : "")
        );
        setSelected(new Set());
      }
    } catch {
      setNudgeError("Couldn't post the nudge. Try again.");
    } finally {
      setNudgeBusy(false);
      setNudgeConfirm(false);
    }
  };

  return (
    <div data-tour="guild-recruit" className="pt-6 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-wider">Onboard</h3>
        <span className="text-xs text-[var(--c-ink-4)]">Find role holders who never signed up</span>
      </div>

      {rolesError ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          {rolesError}
        </div>
      ) : configured === false ? (
        <div className="rounded-2xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] p-8 text-center">
          <div className="text-3xl mb-3 opacity-60">🔗</div>
          <div className="text-sm text-[var(--c-ink-3)]">No Discord server linked.</div>
          <div className="text-xs text-[var(--c-ink-4)] mt-2">
            Invite the Ralli bot to your Discord and link it in guild settings, then you can scan roles here.
          </div>
        </div>
      ) : configured === null ? (
        <div className="text-xs text-[var(--c-ink-4)] py-4">Loading Discord roles…</div>
      ) : (
        <>
          <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 space-y-3">
            <div className="text-[11px] text-[var(--c-ink-3)]">
              Pick a Discord role (like your raider role) and scan it. We match everyone holding it
              against Rallify accounts so you can see who still needs to sign up.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {roles.map(role => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRole(prev => prev === role.id ? null : role.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    selectedRole === role.id
                      ? "bg-amber-500/15 border-amber-400/40 text-amber-800 dark:text-amber-200"
                      : "bg-[var(--c-bg-veil)] border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:border-[var(--c-line-strong)]"
                  }`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                    style={{ background: role.color || "#6b7280" }}
                  />
                  {role.name}
                </button>
              ))}
              {roles.length === 0 && (
                <span className="text-xs text-[var(--c-ink-4)]">No assignable roles found on the linked server.</span>
              )}
            </div>
            <button
              onClick={scan}
              disabled={!selectedRole || scanning}
              className="px-4 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-700 dark:text-amber-300 text-xs font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {scanning ? "Scanning…" : "Scan role"}
            </button>
          </div>

          {scanError && (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              {scanError}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="text-[11px] text-[var(--c-ink-3)]">
                Scanned {result.scanned} member{result.scanned === 1 ? "" : "s"} holding this role
                {result.capped && ` (capped, ${result.totalWithRole} total)`}.
              </div>
              {/* Composer sits above the buckets: it used to land under four
                  member lists, so on a big role nobody ever scrolled to it. */}
              {canCompose && nudgeCfg && (
                <div className="rounded-2xl border border-rose-400/20 bg-rose-500/[0.04] p-4 space-y-3">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-bold text-[var(--c-ink)]">Nudge them in Discord</span>
                        {selected.size > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full border border-rose-400/30 bg-rose-500/15 text-rose-700 dark:text-rose-300 text-[10px] font-bold leading-none">
                            {selected.size} selected
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[var(--c-ink-4)] mt-0.5">
                        One post in your linked channel, @mentioning the people you tick below. Never a DM.
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAll}
                        className="px-2.5 py-1 rounded-lg bg-[var(--c-sunken)] hover:bg-[var(--c-sunken)] border border-[var(--c-line)] text-[10px] font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors"
                      >
                        Select {targets.length > nudgeCfg.maxMentions ? `first ${nudgeCfg.maxMentions}` : "all"}
                      </button>
                      {selected.size > 0 && (
                        <button
                          onClick={() => { setSelected(new Set()); setNudgeConfirm(false); }}
                          className="px-2.5 py-1 rounded-lg bg-[var(--c-sunken)] hover:bg-[var(--c-sunken)] border border-[var(--c-line)] text-[10px] font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <textarea
                    value={nudgeMsg}
                    onChange={e => { setNudgeMsg(e.target.value); setNudgeConfirm(false); }}
                    rows={3}
                    maxLength={nudgeCfg.maxMessageLength}
                    placeholder={nudgeCfg.defaultMessage}
                    className="w-full px-3 py-2 rounded-xl bg-[var(--c-surface)] border border-[var(--c-line)] focus:border-[var(--c-line-strong)] focus:outline-none text-xs text-[var(--c-ink)] placeholder-white/25 resize-none transition-colors"
                  />

                  <div className="flex items-center justify-between gap-3 flex-wrap text-[10px]">
                    <span className="text-[var(--c-ink-4)]">
                      {msgLen === 0
                        ? "Leave blank to send the standard notice above."
                        : "Your wording replaces the standard notice."}
                      {" "}The invite link is always appended.
                    </span>
                    <span className={overLength ? "text-rose-700 dark:text-rose-300 font-bold" : "text-[var(--c-ink-4)]"}>
                      {msgLen}/{nudgeCfg.maxMessageLength}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={sendNudge}
                      disabled={selected.size === 0 || nudgeBusy || overLength}
                      className={`px-4 py-2 rounded-lg border text-xs font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                        nudgeConfirm
                          ? "bg-rose-500/25 hover:bg-rose-500/35 border-rose-400/50 text-rose-800 dark:text-rose-100"
                          : "bg-rose-500/10 hover:bg-rose-500/20 border-rose-400/30 text-rose-700 dark:text-rose-300"
                      }`}
                    >
                      {nudgeBusy
                        ? "Posting…"
                        : nudgeConfirm
                        ? `Confirm: ping ${selected.size} in Discord`
                        : `Post nudge (${selected.size} selected)`}
                    </button>
                    {atCap && (
                      <span className="text-[10px] text-amber-700/70 dark:text-amber-300/70">
                        Cap reached: {nudgeCfg.maxMentions} people per nudge.
                      </span>
                    )}
                    {selected.size > nudgeCfg.mentionsPerPost && (
                      <span className="text-[10px] text-[var(--c-ink-4)]">
                        Splits into {Math.ceil(selected.size / nudgeCfg.mentionsPerPost)} posts
                        ({nudgeCfg.mentionsPerPost} mentions each).
                      </span>
                    )}
                  </div>

                  {nudgeError && (
                    <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-800 dark:text-rose-200">
                      {nudgeError}
                    </div>
                  )}
                  {nudgeDone && (
                    <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-800 dark:text-emerald-200">
                      {nudgeDone}
                    </div>
                  )}
                </div>
              )}

              {SCAN_GROUPS.map(group => (
                <ScanBucket
                  key={group.key}
                  group={group}
                  members={result.buckets[group.key]}
                  selectable={group.key === "not_signed_up" && canCompose}
                  selected={selected}
                  onToggle={toggle}
                  atCap={atCap}
                  startFolded={group.key === "in_guild"}
                />
              ))}

              {nudgeCfg && !nudgeCfg.canNudge && targets.length > 0 && (
                <div className="text-[10px] text-[var(--c-ink-4)]">
                  Only the guild owner can post a nudge to Discord.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
